
import classNames from 'classnames'
import React, { MutableRefObject } from 'react'
import ReactDOMServer from "react-dom/server"
import { ContactMessageDetailsContextProvider } from '../src/contexts/ContactMessageDetailsContext'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { useTranslate } from '../src/hooks/useTranslate'
import { ProductPriceFragment, ProductUnit, ProductVariantFragment, SamplerFragment, StaircaseStepsConfiguratorCornerFragment, StaircaseStepsConfiguratorDataFragment, StaircaseStepsConfiguratorRadiusFragment, StaircaseStepsConfiguratorStraightFragment, UUID } from '../src/model/fragments'
import { getLocale, Locale } from '../src/model/locale'
import { sumPriceArrays } from '../src/utils/formatPrice'
import { numericInputChangeHandler } from '../src/utils/inputChangeHandler'
import { assertDefined } from '../src/utils/isDefined'
import { StaircaseStepsConfiguratorUtils } from '../src/utils/staircaseConfigurator'
import { Button, ButtonView } from './Button'
import { CardView } from './Card'
import { CardBox } from './CardBox'
import { Checkbox } from './Checkbox'
import { Collapsible } from './Collapsible'
import { ContactForm } from './ContactForm'
import { ExportButton } from './ExportButton'
import SvgArrowDown from './icons/SvgArrowDown'
import SvgArrowUp from './icons/SvgArrowUp'
import { Input } from './Input'
import { Picture } from './Picture'
import { ProductVariantPicker } from './ProductVariantPicker'
import { RadioButtons } from './RadioButtons'
import { ShareLink } from './ShareLink'
import { Spacer } from './Spacer'
import { WidthBound } from './WidthBound'

type StaircaseStepsConfiguratorElement = {
	type: 'straight' | 'radius' | 'corner',
	productId: UUID,
	variantId?: UUID,
	length?: number,
	count?: number,
	prices?: ProductPriceFragment[]
};

type StaircaseStepsConfiguratorDTO = {
	elements: StaircaseStepsConfiguratorElement[],
	radiusType: '0' | 'S',
	radiusMultiplier: number | undefined,
	radiusAngularMultiplier: number
	sampler: UUID | null
}

interface StaircaseStepsConfiguratorCommonProps
{
	locales: Locale[]
	elements: StaircaseStepsConfiguratorElement[],
	shareData: StaircaseStepsConfiguratorDTO | null,
	onChangeElements?: (elems: StaircaseStepsConfiguratorElement[], type: 'straight' | 'radius' | 'corner') => void
}

/**************************************************************** */
/** Krok 1 - prime stupne *************************************** */
/**************************************************************** */
interface StaircaseStepsConfiguratorStraightProps extends StaircaseStepsConfiguratorCommonProps
{
	straights: StaircaseStepsConfiguratorStraightFragment[]
}

const StaircaseStepsConfiguratorStraight : React.FunctionComponent<StaircaseStepsConfiguratorStraightProps> = 
	({ straights, elements, shareData, onChangeElements, locales }) => {

		const translate = useTranslate();
		const localization = useLocalization();
		const [inputElements, setInputElements] = React.useState<StaircaseStepsConfiguratorElement[]>([]);

		if(!straights.length)
		{
			return <></>
		}

		React.useEffect(() => {
			const items = inputElements.filter(i => i.count && i.variantId && i.length);
			onChangeElements!(items, 'straight');
		}, [inputElements]);

		React.useEffect(() => {
			setInputElements(straights.map(s => {
				return {
					type: 'straight',
					productId: s.product.id,
					length: s.minLength
				}
			}));
	
		}, []);
	
		React.useEffect(() => {
	
			if(shareData)
			{
				setInputElements(straights.map(s => {
					const existing = shareData.elements.find(e => e.type == 'straight' && e.productId == s.product.id);
					return existing ?? {
						type: 'straight',
						productId: s.product.id,
						length: s.minLength
					}
				}));
			}
	
		}, [shareData]);

		return (
			<>
				<div  className={classNames('staircaseStepConfiguratorLayout-state', 'staircaseStepConfiguratorLayout-straights')}>
					<header>
						<div dangerouslySetInnerHTML={{ __html: localization.translate('staircaseStepConfiguratorLayout-straight-text') }}></div>
					</header>
					<div>	
						<div className='productTable'>
							<table>
								<thead>
									<tr>
										<th>{ localization.translate('product-name-header') }</th>
										<th className='dimension'>{ localization.translate('dimensions') }<br/>{ localization.translate('dimensionsDescription') }</th>
										<th className='variant'>{ localization.translate('variant') }</th>
										<th className='price'>{ localization.translate('product-price-vat-header') }</th>
										<th className='count'>{ localization.translate('products-items') }</th>
										<th className='price'>{ localization.translate('product-total-price-vat-header') }</th>
									</tr>
								</thead>
								<tbody>
									{
										inputElements.map(elem => {
											const s = straights.find(s => s.product.id == elem.productId);
											assertDefined(s);

											const data = getLocale(s.product.locales, locales);
											if(!data)
											{
												return <></>;
											}	
											const name = data.name;

											return <tr>
												<th>
													<div>
														<span>{ name }</span>
														<Picture alt={ data.name } image={s.product.listingImage} width={128} height={128} />
													</div>
													
												</th>
												<td className='dimension'>
													<Input label={ localization.translate('length') } value={elem.length || ''} min={s.minLength} max={s.maxLength} align={'right'}
													
													onChange={(e) => numericInputChangeHandler(e, (val) => {

														const len = parseInt(val);
														elem!.length = len;
														setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);
													
													}, () => {}) }

													onBlur={(e) => {
														const len = Math.ceil(elem!.length!);
														elem!.length = len % 5 === 0 ? len : (len + (5 - len % 5));

														if(elem!.length < s.minLength)
														{
															elem!.length = s.minLength;
														}
														else if(elem!.length > s.maxLength)
														{
															elem!.length = s.maxLength;
														}

														setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);
													}}

													onKeyUp={(e) => {
														if(e.code == 'Enter' || e.code == 'NumpadEnter')
														{
															const len = Math.ceil(elem!.length!);
															elem!.length = len % 5 === 0 ? len : (len + (5 - len % 5));

															if(elem!.length < s.minLength)
															{
																elem!.length = s.minLength;
															}
															else if(elem!.length > s.maxLength)
															{
																elem!.length = s.maxLength;
															}

															setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);
														}
													}}

													
													/> <span>x { s.product.attributes.width } x { s.product.attributes.height.toFixed(0) }</span><br/>
													<span className='small'>{ localization.translate('staircaseStepConfiguratorLayout-enterLengthRange') } { s.minLength } - { s.maxLength }mm</span>
												</td>
												<td className='variant'>
													<ProductVariantPicker
														priceMultiplier={(elem?.length ?? 1000.0) / 1000.0}
														items={(s.product.variants)}
														selectedId={elem.variantId}
														highlight={(!elem.variantId && elem.count) as boolean}
														onChange={(val) => {
															elem!.variantId = val;
															setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);
														} }
													/>
												</td>
												<td className='price'>
													{localization.formatPricesArray(localization.extractPrices(StaircaseStepsConfiguratorUtils.getPriceForStraightStep(straights.find(s => s.product.id === elem.productId)!.product, elem.length, elem!.variantId, localization.extractPrices(straights.find(s => s.product.id === elem.productId)!.priceCorrections), true)), 1, true).join(', ').trim() || '-'}
												</td>
												<td className='count'>
													<Input label={'ks'} align={'right'} value={elem.count || ''} min={1} max={1000} 
													
													onChange={(e) => numericInputChangeHandler(e, (val) => {

														elem!.count = parseInt(val);
														setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);

													}, () => {}) }

													/>
												</td>
												<td className='price'>
													<span>{ localization.translate('product-total-price-vat-header')}:: </span>{ (elem.count ? localization.formatPricesArray(localization.extractPrices(StaircaseStepsConfiguratorUtils.getPriceForStraightStep(straights.find(s => s.product.id === elem.productId)!.product, elem.length, elem!.variantId, localization.extractPrices(straights.find(s => s.product.id === elem.productId)!.priceCorrections), false)), elem.count, true).join(', ').trim() : '-') ?? '-'}
												</td>
											</tr>
										})
									}
								</tbody>
							
							</table>
						</div>
					</div>
					<Spacer initial={60} />
				</div>
			</>
		);
};

/**************************************************************** */
/** Krok 2 - obloukove stupne *********************************** */
/**************************************************************** */
interface StaircaseStepsConfiguratorRadiusProps extends StaircaseStepsConfiguratorCommonProps
{
	radii: StaircaseStepsConfiguratorRadiusFragment[],
	radiusType: '0' | 'S',
	radiusMultiplier: number | undefined,
	radiusAngularMultiplier: number,
	sampler?: SamplerFragment,
	onChangeSettings: (radiusType: '0' | 'S', radiusMultiplier: number | undefined, radiusAngularMultiplier: number, sampler?: SamplerFragment) => void
}

const StaircaseStepsConfiguratorRadius : React.FunctionComponent<StaircaseStepsConfiguratorRadiusProps> = 
	({ radii, elements, locales, shareData, radiusType, radiusMultiplier, radiusAngularMultiplier, sampler, onChangeElements, onChangeSettings }) => {

	if(!radii.length)
	{
		return <></>
	}

	type StaircaseStepsConfiguratorElementWithLocation = StaircaseStepsConfiguratorElement & {
		location: string
	}
		
	const localization = useLocalization();
	const [radiusMultiplierInput, setRadiusMultiplierInput] = React.useState<string>((radiusMultiplier ?? 1).toString());
	const [inputElements, setInputElements] = React.useState<StaircaseStepsConfiguratorElementWithLocation[]>([]);
	const [selection, setSelection] = React.useState<{[prop: string]: boolean}>({});
	const [tableCollapsed, setTableCollapsed] = React.useState<boolean>(true);
	const selectedCount = Object.entries(selection).reduce((acc, it) => it[1] ? acc + 1 : acc, 0);

	// Handles changes in input elements and propagates them to the upper level real elements
	React.useEffect(() => {
		const items = inputElements.filter(i => i.count && i.variantId);
		onChangeElements!(items, 'radius');
	}, [inputElements]);

	// Updates counts in input elements upon change of any other states
	React.useEffect(() => {
		setInputElements(inputElements.map((e) => {
			
			return ({
				...e,
				count: (radiusMultiplier && selection[e.location]) ? (radii.find(r => r.product.id == e.productId)?.count ?? 0) * radiusAngularMultiplier * radiusMultiplier : 0
			})
		}));
	}, [selection, radiusMultiplier, radiusAngularMultiplier]);

	const samplers : SamplerFragment[] = React.useMemo(() => {
		
		const samplerValues : {[prop: string] : SamplerFragment} = {};
		const samplerCounts : {[prop: string] : number} = {};

		for(const radius of radii)
		{
			for(const variant of radius.product.variants)
			{
				if(!samplerCounts[variant.sampler.id])
				{
					samplerCounts[variant.sampler.id] = 0;
				}

				samplerCounts[variant.sampler.id]++;
				samplerValues[variant.sampler.id] = variant.sampler;
			}
		}

		const max = Object.entries(samplerCounts).reduce((acc, e) => {
			return Math.max(acc, e[1]);
		}, 0);

		const ids = Object.entries(samplerCounts).filter(e => e[1] == max).map(e => e[0]);
		const result = Object.entries(samplerValues).filter(e => ids.includes(e[0])).map(e => e[1]);

		return result as SamplerFragment[];
	}, [radii]);

	// Initial setup 
	React.useEffect(() => {

		let s = sampler;
		if(samplers.length && !sampler)
		{
			s = samplers[0];
		}

		const [items, selection] = [-1,0,1,2,3,4,5,6,7,8,9,10].reduce((acc, location) => {
			const loc = (location == -1) ? 'S' : location.toString(); 
			const r = radii.find(r => r.location == loc);

			if(!r)
			{
				return acc;
			}

			acc[0].push({
				type: 'radius',
				productId: r.product.id,
				location: loc,
				variantId: s ? r.product.variants.find(vv => vv.sampler.id == s!.id)?.id : undefined
			});
			acc[1][loc] = false;

			return acc;
		}, [[] as StaircaseStepsConfiguratorElementWithLocation[], {} as {[prop: string]: boolean}]);

		setInputElements(items);
		setSelection(selection);

		onChangeSettings(radiusType, radiusMultiplier, radiusAngularMultiplier, s);
	}, []);

	

	// Share
	React.useEffect(() => {

		if(shareData)
		{
			const items = inputElements.map(elem => {
				const shared = shareData.elements.find(e => e.type == 'radius' && e.productId == elem.productId);
				const r = radii.find(r => r.product.id == elem.productId);
				return shared ? ({...shared, location: r!.location}) : elem
			})

			setInputElements(items);
			setSelection(Object.fromEntries(Object.entries(selection).map(entry => {

				const item = items.find(it => it.location == entry[0]);
				return [entry[0], item && item.count ? true : entry[1]];
			})));

			setRadiusMultiplierInput(shareData.radiusMultiplier!.toString());

			let s = sampler;
			if(shareData.sampler)
			{
				s = samplers.find(ss => ss.id == shareData.sampler);
			}

			onChangeSettings(shareData.radiusType, shareData.radiusMultiplier, shareData.radiusAngularMultiplier, s);
		}

	}, [shareData]);
	
	const totalHeight = elements
	.filter(e => e.type == 'radius')
	.map(e => radii.find(r => r.product.id == e.productId)?.product.attributes.height ?? 0)
	.reduce((acc, val) => acc + val, 0);

	const totalPrice = sumPriceArrays(elements
		.filter(e => e.type == 'radius' && e.count)
		.map(e => 
			localization.extractPrices(radii.find(r => r.product.id == e.productId)?.product.variants.find(v => v.id === e.variantId)?.prices ?? [])
			.map(pr => ({
				...pr,
				cents: pr.cents * e.count!
			}))
		)
	)

	const updateInputElementsForSampler = (ss?: SamplerFragment) => {
		if(ss && inputElements.length)
		{
			const elems = inputElements.map(e => {

				const vvv = radii.find(rr => rr.product.id == e.productId)?.product.variants.find(vv => vv.sampler.id == ss.id);
				return {
					...e,
					variantId: vvv?.id ?? e.variantId
				}

			});
			
			setInputElements(elems);
		}
	};

	return (
		<>
			<div className={classNames('staircaseStepConfiguratorLayout-state', 'staircaseStepConfiguratorLayout-radii')}>
				<header>
					<div dangerouslySetInnerHTML={{ __html: localization.translate('staircaseStepConfiguratorLayout-radii-text') }}></div>
				</header>
				<div>	

				<div className="staircaseStepConfiguratorLayout-radii-grid">
					<div className='flex'>
						<div dangerouslySetInnerHTML={{ __html: localization.translate('staircaseStepConfiguratorLayout-radii-additional-text') }}></div>
						<div>
							<div className='flex-inner'>
								<div >
									<h4>{ localization.translate('staircaseStepConfiguratorLayout-radii-radii-angle') }</h4>
									<div>
										<RadioButtons 
											initialIndex={radiusAngularMultiplier - 1} 
											desiredIndex={radiusAngularMultiplier - 1}

											items={[
												{
													text: ' 90°'
												},
												// {
												// 	text: '180°'
												// }
											]} 
										
											onChange={(index) =>
											{
												return onChangeSettings(radiusType, radiusMultiplier, index + 1, sampler);
											} }
										/>
									</div>
								</div>
								<div>
									x
								</div>
								<div>
									<h4>{ localization.translate('staircaseStepConfiguratorLayout-radii-radii-count') }</h4>
									<Input label={ localization.translate('staircaseStepConfiguratorLayout-radii-radii-count') } align={'right'} value={radiusMultiplierInput} min={1} max={20} 
									
										onChange={(e) => numericInputChangeHandler(e, setRadiusMultiplierInput, (val) => {
											return onChangeSettings(radiusType, val, radiusAngularMultiplier, sampler)
										}) }

									/>
								</div>
								
							</div>

							<div>
								<h4>{ localization.translate('staircaseStepConfiguratorLayout-radii-top') }</h4>	
								<RadioButtons 
									initialIndex={radiusType === '0' ? 1 : 0} 
									desiredIndex={radiusType === '0' ? 1 : 0} 
									
									items={[
										{
											text: localization.translate('staircaseStepConfiguratorLayout-centerLanding')
										},
										{
											text: localization.translate('staircaseStepConfiguratorLayout-centerStep') 
										}
									]} 
								
									onChange={(index) => {
										const old = radiusType;
										const n = index === 0 ? 'S' : '0';

										if(n != old)
										{
											setSelection(Object.fromEntries(Object.entries(selection).map(entry => {
												return [entry[0], entry[0] == old ? false : entry[1]];
											})));

											onChangeSettings(n, radiusMultiplier, radiusAngularMultiplier, sampler)
										}
									}}
								/>
							</div>

							<div className="staircaseStepConfiguratorLayout-radii-sampler">
								<h4>{ localization.translate('staircaseStepConfiguratorLayout-radii-sampler') }</h4>
								<div className="grid--samplers">
									{
										samplers.map(ss => {
											
											const data = getLocale(ss.locales, locales);
											
											return data && <CardBox
												isActive={ sampler?.id == ss.id }
												onClick={() => {
													onChangeSettings(radiusType, radiusMultiplier, radiusAngularMultiplier, ss);
													updateInputElementsForSampler(ss);
												}}
												title={''}
												categoryTag={''}
												view={CardView.default}
												content={
													<div className="flex">
														<div>
															<Picture
																image={ss.image}
																width={70}
																height={70}
																alt={data.name}
																
															/>
														</div>
														<div>
															<h3 dangerouslySetInnerHTML={{__html: data.name}}></h3>
														</div>
													</div>
												}
											/>;

										})
									}
								</div>
							</div>
						</div>
						
					</div>

					<StaircasesSVGComponent setSelection={setSelection} selection={selection} radiusType={radiusType} />
					<p className="staircaseStepConfiguratorLayout-radii-image-helper-text">{ localization.translate('staircaseStepConfiguratorLayout-radii-helper-text') }</p>
				</div>

				<div className='staircaseStepConfiguratorLayout-radii-summary'>
					<table>
						<tr>
							<td>{ localization.translate('staircaseStepConfiguratorLayout-radii-height') }:</td>
							<td>{ totalHeight > 0 ? <strong>{ totalHeight }mm</strong> : <strong>{ localization.translate('select-option') }</strong>}</td>
						</tr>
						<tr>
							<td>{ localization.translate('product-total-price-vat-header')}:</td>
							<td>{ totalHeight > 0 ? <strong>{ localization.formatPricesArray(totalPrice, 1, true).join(', ') }</strong> : <strong>{ localization.translate('select-option') }</strong>}</td>
						</tr>
					</table>
				</div>
				
				<div className='staircaseStepConfiguratorLayout-radii-collapsible-trigger'>
					<Button view={ButtonView.outline} icon={tableCollapsed ? <SvgArrowDown/> : <SvgArrowUp />} onClick={(e) => {
						e.preventDefault();
						setTableCollapsed(!tableCollapsed);
					}}>	
						{ localization.translate(tableCollapsed ? 'table-show' : 'table-hide')}
					</Button>
				</div>
				<div className={classNames('staircaseStepConfiguratorLayout-radii-collapsible', tableCollapsed && 'staircaseStepConfiguratorLayout-radii-collapsible--collapsed')}>
					<div className="productTable">
						<table>
								<thead>
										<tr>
											<th></th>
											<th>{ localization.translate('product-name-header') }</th>
											<th className='dimension'>{ localization.translate('dimensions') }<br/>{ localization.translate('dimensionsDescription') }</th>
											<th className='variant'>{ localization.translate('variant') }</th>
											<th className='price'>{ localization.translate('product-price-vat-header') }</th>
											<th className='count'>{ localization.translate('products-items') }</th>
											<th className='price'>{ localization.translate('product-total-price-vat-header') }</th>
										</tr>
									</thead>
									<tbody>
										{
											inputElements.map(elem => {

												if((elem.location === 'S' && radiusType != 'S') || (elem.location === '0' && radiusType !== '0'))
												{
													return <></>
												}

												const r = radii.find(r => r.product.id == elem.productId)!;
												assertDefined(r);

												const data = getLocale(r.product.locales, locales);
												if(!data)
												{
													return <></>;
												}

												const isEnabled = StaircaseStepsConfiguratorUtils.determineRadiusStageAvailability(elem.location, radiusType, selection);
												const prices = (elem.variantId && selection[elem.location]) ? localization.extractPrices(r.product.variants.find(v => v.id == elem!.variantId)!.prices).map(pr => ({...(pr as ProductPriceFragment), unit: ProductUnit.item})) : [];
												
												return <tr>
													<td>
														<Checkbox 
														
														control={{ 
															
															value: 1, 
															disabled: !isEnabled,
															checked: selection[elem.location] 
														}}
														
														onChange={(checked) => {
															if(isEnabled)
															{
																setSelection(Object.fromEntries(Object.entries(selection).map(entry => [
																	entry[0],
																	entry[0] === elem.location ? checked : entry[1]
																])));
															}
														}}

														label={
															<>
																Řada&nbsp;{ elem.location }
															</>}  
															
														/>
													</td>
													<th className='picture'>
														<div>
															<p>
																{ data.name }
															</p>
															
															<Picture alt={ data.name } image={r.product.listingImage } width={75} height={75} mode={'contain'} />
														</div>
														
													</th>
													<td className='dimension'>
														<span>{ r.product.attributes.length } x { r.product.attributes.width } x { r.product.attributes.height }<br/>{ localization.translate('staircaseStepConfiguratorLayout-radii-outer-radius') } { r.radius }</span>
													</td>
													<td className='variant'>
														<ProductVariantPicker
															items={(r.product.variants)}
															selectedId={elem.variantId}
															readonly
															highlight={(!elem.variantId && elem.count) as boolean}
														/>
													</td>
													<td className='price'>
														{localization.formatPricesArray(prices, 1, true).join(', ').trim() || '-'}
													</td>
													<td className='count'>
														{ elem.count! > 0 ? elem.count : '-' }
													</td>
													<td className='price'>
														<span>{ localization.translate('product-total-price-vat-header')}: </span>{ localization.formatPricesArray(prices.map(pr => ({...pr, unit: undefined})), elem.count, true).join(', ').trim() || '-'}
													</td>
												</tr>
											})
										}
									</tbody>
							</table>
						</div>

						<div className='staircaseStepConfiguratorLayout-radii-summary'>
							<table>
								<tr>
									<td>{ localization.translate('staircaseStepConfiguratorLayout-radii-height') }:</td>
									<td>{ totalHeight > 0 ? <strong>{ totalHeight }mm</strong> : <strong>{ localization.translate('select-option') }</strong>}</td>
								</tr>
								<tr>
									<td>{ localization.translate('product-total-price-vat-header')}:</td>
									<td>{ totalHeight > 0 ? <strong>{ localization.formatPricesArray(totalPrice, 1, true).join(', ') }</strong> : <strong>{ localization.translate('select-option') }</strong>}</td>
								</tr>
							</table>
						</div>
					</div>
				</div>
				<Spacer initial={60} />
			</div>
		</>
	);
};

/**************************************************************** */
/** Krok 3 - rohove stupne ************************************** */
/**************************************************************** */
interface StaircaseStepsConfiguratorCornerProps extends StaircaseStepsConfiguratorCommonProps
{
	corners: StaircaseStepsConfiguratorCornerFragment[]
}

const StaircaseStepsConfiguratorCorner : React.FunctionComponent<StaircaseStepsConfiguratorCornerProps> = 
	({ corners, elements, locales, shareData, onChangeElements }) => {
		
	const localization = useLocalization();
	const [inputElements, setInputElements] = React.useState<StaircaseStepsConfiguratorElement[]>([]);

	if(!corners.length)
	{
		return <></>
	}

	React.useEffect(() => {

		setInputElements(corners.map(c => {
			return {
				type: 'corner',
				productId: c.product.id
			}
		}));

	}, []);

	React.useEffect(() => {
		const items = inputElements.filter(i => i.count && i.variantId);
		onChangeElements!(items, 'corner');
	}, [inputElements]);

	React.useEffect(() => {

		if(shareData)
		{
			setInputElements(corners.map(c => {
				const existing = shareData.elements.find(e => e.type == 'corner' && e.productId == c.product.id);
				return existing ?? {
					type: 'corner',
					productId: c.product.id
				}
			}));
		}

	}, [shareData]);
	
	return (<>
			<div className={classNames('staircaseStepConfiguratorLayout-state', 'staircaseStepConfiguratorLayout-corners')}>
				<header>
					<div dangerouslySetInnerHTML={{ __html: localization.translate('staircaseStepConfiguratorLayout-corners-text') }}></div>
				</header>
				<div>	
					<div className='productTable'>
						<table>
							<thead>
									<tr>
										<th></th>
										<th className='variant'>{ localization.translate('variant') }</th>
										<th className='price'>{ localization.translate('product-price-vat-header') }</th>
										<th className='count'>{ localization.translate('products-items') }</th>
										<th className='price'>{ localization.translate('product-total-price-vat-header') }</th>
									</tr>
								</thead>
								<tbody>
									{
										inputElements.map(elem => {
											const c = corners.find(c => c.product.id == elem.productId);
											assertDefined(c);
											
											const data = getLocale(c.product.locales, locales);
											if(!data)
											{
												return <></>;
											}

											const prices = elem.variantId ? localization.extractPrices(c.product.variants.find(v => v.id == elem!.variantId)!.prices).map(pr => ({...(pr as ProductPriceFragment), unit: ProductUnit.item})) : [];
											
											return <tr>
												<th className='picture'>
													<div>
														<p>
															{ data.name }<br/>
															<span className='small'>{ data.altName }</span>
														</p>
														
														<Picture alt={ data.name } image={c.product.listingImage } width={128} height={128} />
													</div>
													
												</th>
												<td className='variant'>
													<ProductVariantPicker
														items={(c.product.variants)}
														selectedId={elem.variantId}
														highlight={(!elem.variantId && elem.count) as boolean}
														onChange={(val) => {
															elem!.variantId = val;
															setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);
														} }
													/>
												</td>
												<td className='price'>
													{localization.formatPricesArray(prices, 1, true).join(', ').trim() || '-'}
												</td>
												<td className='count'>
													<Input label={'ks'} align={'right'} value={elem.count || ''} min={1} max={1000} 
													
														onChange={(e) => numericInputChangeHandler(e, (val) => {

															elem!.count = parseInt(val);
															setInputElements([...inputElements.map(e => e == elem ? elem! : e)]);

														}, () => {}) }

													/>
													
												</td>
												<td className='price'>
													<span>{ localization.translate('product-total-price-vat-header')}: </span>{ (elem.count ? localization.formatPricesArray(prices.map(pr => ({...pr, unit: undefined})), elem.count, true).join(', ').trim() : '-') ?? '-'}
												</td>
											</tr>
										})
									}
								</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	);
}

/**************************************************************** */
/** Krok 4 - zobrazeni vysledku  ******************************** */
/**************************************************************** */
interface StaircaseStepsConfiguratorFinalProps extends StaircaseStepsConfiguratorCommonProps
{
	straights: StaircaseStepsConfiguratorStraightFragment[],
	radii: StaircaseStepsConfiguratorRadiusFragment[],
	corners: StaircaseStepsConfiguratorCornerFragment[],
	sampler?: SamplerFragment,
	radiusType: '0' | 'S',
	radiusMultiplier: number | undefined,
	radiusAngularMultiplier: number
}

const StaircaseStepsConfiguratorFinal : React.FunctionComponent<StaircaseStepsConfiguratorFinalProps> = 
	({ locales, elements, straights, radii, corners, sampler, radiusType, radiusMultiplier, radiusAngularMultiplier }) => {

	const localization = useLocalization();
	const total = elements.reduce((acc, elem) => acc + (elem.count ?? 0), 0);
	const totalPrice = sumPriceArrays(elements
		.filter(e =>  e.count)
		.map(e => 
		{
			if(e.type === 'straight')
			{
				const s = straights.find(r => r.product.id == e.productId);
				return localization.extractPrices(StaircaseStepsConfiguratorUtils.getPriceForStraightStep(s!.product, e.length, e.variantId, localization.extractPrices(straights.find(s => s.product.id === e.productId)!.priceCorrections), false));
			}

			return localization.extractPrices((e.type == 'radius' ? radii : corners).find(r => r.product.id == e.productId)?.product.variants.find(v => v.id === e.variantId)?.prices ?? [])
			.map(pr => ({
				...pr,
				cents: pr.cents * e.count!
			}))
		})
	) ?? 0;

	const getSamplerName = (variant : ProductVariantFragment) => {
		const data = getLocale(variant.sampler.locales, locales);
		return data?.name;
	};

	const link = 
		globalThis.window ? globalThis.window.location.toString().replace(/\#.*$/g, '').replace(/\?.*$/g, '') + '?share=' + 
		encodeURIComponent(Buffer.from(JSON.stringify({
			elements: elements,
			radiusAngularMultiplier: radiusAngularMultiplier,
			radiusMultiplier: radiusMultiplier,
			radiusType: radiusType,
			sampler: sampler?.id ?? null
		} as StaircaseStepsConfiguratorDTO), 'utf8').toString('base64')) + '#tab-konfigurator' : '#';

	const makeTable = React.useCallback(() => {
		return <>
		<h3>{ localization.translate('staircaseStepConfiguratorLayout-title-calculation') }:</h3>
				<table>
					<thead>
						<tr>
							<th></th>
							<th></th>
							<th></th>
							<th>{ localization.translate('noVAT') }</th>
							<th>{ localization.translate('inclVAT') }</th>
						</tr>
					</thead>
					<tbody>
						{
							elements.filter(elem => elem.count && elem.type == 'straight').map(elem => {

								const s = straights.find(s => s.product.id == elem.productId);
								const variant = s?.product.variants.find(v => v.id == elem.variantId);
								const locale = getLocale(s?.product.locales, locales);
								if(!s || !variant || !locale)
								{
									return <></>
								}

								const prices = localization.extractPrices(StaircaseStepsConfiguratorUtils.getPriceForStraightStep(s.product, elem.length, variant.id, localization.extractPrices(straights.find(s => s.product.id === elem.productId)!.priceCorrections), false));

								return <tr>
									<td>{ locale.name } { elem.length }mm</td>
									<td>{ getSamplerName(variant) }</td>
									<td>{ elem.count }ks</td>
									<td><strong>{ localization.formatPricesArray(prices, elem.count, false).join(', ') }</strong></td>
									<td><strong>{ localization.formatPricesArray(prices, elem.count, true).join(', ') }</strong></td>
								</tr>
							})
						}

						{
							elements.filter(elem => elem.count && elem.type == 'radius').map(elem => {

								const r = radii.find(r => r.product.id == elem.productId);
								const variant = r?.product.variants.find(v => v.id == elem.variantId);
								const locale = getLocale(r?.product.locales, locales);
								if(!variant || !locale)
								{
									return <></>
								}

								return <tr>
									<td>{ locale.name } <span className='text-muted'>(ID { variant.externalIdentifier })</span></td>
									<td>{ getSamplerName(variant) }</td>
									<td>{ elem.count }ks</td>
									<td><strong>{ localization.formatPricesArray(localization.extractPrices(variant!.prices), elem.count, false).join(', ') }</strong></td>
									<td><strong>{ localization.formatPricesArray(localization.extractPrices(variant!.prices), elem.count, true).join(', ') }</strong></td>
								</tr>
							})
						}

						{
							elements.filter(elem => elem.count && elem.type == 'corner').map(elem => {

								const c = corners.find(c => c.product.id == elem.productId);
								const variant = c?.product.variants.find(v => v.id == elem.variantId);
								const locale = getLocale(c?.product.locales, locales);
								if(!variant || !locale)
								{
									return <></>
								}

								return <tr>
									<td>{ locale.name } {locale.altName}</td>
									<td>{ getSamplerName(variant) }</td>
									<td>{ elem.count }ks</td>
									<td><strong>{ localization.formatPricesArray(localization.extractPrices(variant!.prices), elem.count, false).join(', ') }</strong></td>
									<td><strong>{ localization.formatPricesArray(localization.extractPrices(variant!.prices), elem.count, true).join(', ') }</strong></td>
								</tr>
							})
						}
						
					</tbody>
					<tfoot>
						<tr>
							<td><strong>{ localization.translate('productPriceTotal') } </strong></td>
							<td></td>
							<td></td>
							<td><strong>{ localization.formatPricesArray(totalPrice, undefined, false).join(', ') || '-' }</strong></td>
							<td><strong>{ localization.formatPricesArray(totalPrice, 1, true).join(', ') || '-' }</strong></td>
						</tr>
					</tfoot>
				</table>
		</>;
	}, [elements]);
	
	const gridRef = React.useRef() as MutableRefObject<HTMLDivElement>;
	return (
		<>
			<Spacer initial={100}/>
			<div className={classNames('staircaseStepConfiguratorLayout-state', 'staircaseStepConfiguratorLayout-final')}>
				<header>
					<h2>{ localization.translate('project-summary') } <span className='number'>{total}</span></h2>
					<p></p>
				</header>
				<div ref={gridRef} className="grid--price">
					{ makeTable() }
				</div>
				{ total > 0 && <>
					<Spacer initial={30} />
					<div className="grid--share">
						<ShareLink link={link} />
						<ExportButton 
							filename='presbeton_schodistove_stupne' 
							landscape={true} 
							documentClassName='staircaseStepConfiguratorLayout-export'
						>{ localization.translate('print') }</ExportButton>
					</div>
					<Spacer initial={30} />
					<div>
						<WidthBound size='narrow'>
							<h3>{ localization.translate('project-send') }</h3>
							<ContactMessageDetailsContextProvider type='configurator' subject={ localization.translate('staircaseStepConfiguratorLayout-emailSubject') } emailContentCallback={() => {
								return gridRef.current.innerHTML;
							}}
							>
								<ContactForm
									formAdminEmail="marek.mastny@presbeton.cz"
									note={
										localization.translate('staircaseStepConfiguratorLayout-calculationNote')
									}
									requireCity={true}
									requireFormFields={true}
								/>
							</ContactMessageDetailsContextProvider>
						</WidthBound>
					</div>
				</>}
				<Spacer initial={60} />
			</div>
		</>
	);
};

export type StaircaseStepConfiguratorLayoutProps = StaircaseStepsConfiguratorDataFragment & {
	locales: Locale[]
}

export const StaircaseStepConfiguratorLayout: React.FunctionComponent<StaircaseStepConfiguratorLayoutProps> =
	({ locales, straights, radii, corners }) => {

		const localization = useLocalization();
		const [elements, setElements] = React.useState<StaircaseStepsConfiguratorElement[]>([]);	
		const [shareData, setShareData] = React.useState<StaircaseStepsConfiguratorDTO | null>(null);
		const [radiusType, setRadiusType] = React.useState<'0' | 'S'>('S');
		const [radiusMultiplier, setRadiusMultiplier] = React.useState<number | undefined>(1);
		const [radiusAngularMultiplier, setRadiusAngularMultiplier] = React.useState<number>(1);
		const [sampler, setSampler] = React.useState<SamplerFragment | undefined>(undefined);

		React.useEffect(() => {

			if(!globalThis.window)
			{
				return;
			}

			const param = Object.fromEntries(window.location.search.replace('?', '').split('&').map(str => str.split('=')));
			if(param['share'] !== undefined)
			{
				const buffer = Buffer.from(decodeURIComponent(param['share']), 'base64');
				const json = JSON.parse(buffer.toString('utf8'));

				if(json['elements'] && json['elements'].length)
				{
					const dto = json as StaircaseStepsConfiguratorDTO;
					setShareData(dto);
					setElements(dto.elements);
				}
			}

		}, []);

		const straightsCount = elements.filter(elem => elem.type == 'straight').reduce((acc, elem) => acc + (elem.count ?? 0), 0);
		const radiiCount = elements.filter(elem => elem.type == 'radius').reduce((acc, elem) => acc + (elem.count ?? 0), 0);
		const cornersCount = elements.filter(elem => elem.type == 'corner').reduce((acc, elem) => acc + (elem.count ?? 0), 0);

		const title = localization.translate('staircaseStepConfiguratorLayout-title');
		return (
				<div id="konfigurator" className="staircaseStepsConfiguratorLayout">
						<div className="staircaseStepsConfiguratorLayout-inner">
							{
								title && <h2>{ title }</h2>
							}
							
							<div className='staircaseStepsConfiguratorLayout-inner-perex' dangerouslySetInnerHTML={{ __html: localization.translate('staircaseStepConfiguratorLayout-perex') }} />

							<Collapsible 
								isPrintable={straightsCount > 0}
								label={ <h2><StraightSVG /> {localization.translate('staircaseStepConfiguratorLayout-straight')} <span className='number' data-count={straightsCount}>{ straightsCount }</span> </h2> }
								content={
									<StaircaseStepsConfiguratorStraight
										straights={straights}
										shareData={shareData}
										elements={elements.filter(el => el.type == 'straight')}
										locales={locales}
										onChangeElements={(elems) => {
											setElements(
												elements.filter(el => el.type != 'straight').concat(elems)
											);
										} }
									/>

								}
							/>

							<Collapsible 
								isPrintable={radiiCount > 0}
								label={ <h2><RadiiSVG/> {localization.translate('staircaseStepConfiguratorLayout-radii')} <span className='number' data-count={radiiCount}>{ radiiCount }</span></h2> }
								content={
									<StaircaseStepsConfiguratorRadius
										sampler={sampler}
										radii={radii}
										radiusType={radiusType}
										radiusAngularMultiplier={radiusAngularMultiplier}
										radiusMultiplier={radiusMultiplier}
										shareData={shareData}
										elements={elements.filter(el => el.type == 'radius')}
										locales={locales}
										onChangeElements={(elems) => {
											setElements(
												elements.filter(el => el.type != 'radius').concat(elems)
											);
										} }
										onChangeSettings={(type, multiplier, angularMultiplier, sampler) => {
											setRadiusType(type);
											setRadiusMultiplier(multiplier);
											setRadiusAngularMultiplier(angularMultiplier);
											setSampler(sampler);
										}}
									/>
								}
							/>

							<Collapsible 
								isPrintable={ cornersCount > 0}
								label={ <h2><CornerSVG/> { localization.translate('staircaseStepConfiguratorLayout-corners') } <span className='number' data-count={cornersCount}>{ cornersCount }</span> </h2> }
								content={
									<StaircaseStepsConfiguratorCorner
										corners={corners}
										shareData={shareData}
										elements={elements.filter(el => el.type == 'corner')}
										locales={locales}
										onChangeElements={(elems) => {
											setElements(
												elements.filter(el => el.type != 'corner').concat(elems)
											);
										} }
									/>
								}
							/>

							<StaircaseStepsConfiguratorFinal
								elements={elements}
								shareData={shareData}
								locales={locales}
								straights={straights}
								radii={radii}
								corners={corners}
								sampler={sampler}
								radiusAngularMultiplier={radiusAngularMultiplier}
								radiusMultiplier={radiusMultiplier}
								radiusType={radiusType}
							/>

						</div>

				</div>
			
		);
	}

	const StraightSVG : React.FunctionComponent = () => <svg id="Vrstva_1" style={{ verticalAlign: 'middle', marginRight: '5px' }} data-name="Vrstva 1" xmlns="http://www.w3.org/2000/svg" width="40px" height="40px" viewBox="0 0 99.221 99.221">
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="14.44 63.939 13.611 55.182 32.702 68.602 32.953 77.451 14.44 63.939"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="14.44 63.939 13.611 55.182 32.702 68.602 32.953 77.451 14.44 63.939"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="66.972 21.77 85.611 31.663 32.702 68.602 13.611 55.182 66.972 21.77"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="66.972 21.77 85.611 31.663 32.702 68.602 13.611 55.182 66.972 21.77"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="85.61 31.661 84.556 40.141 32.953 77.451 32.702 68.602 85.61 31.661"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="85.61 31.661 84.556 40.141 32.953 77.451 32.702 68.602 85.61 31.661"/>
	</svg>

	const RadiiSVG : React.FunctionComponent = () => <svg id="Vrstva_1" style={{ verticalAlign: 'middle', marginRight: '5px' }} data-name="Vrstva 1" xmlns="http://www.w3.org/2000/svg" width="40px" height="40px" viewBox="0 0 99.221 99.221">
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m64.211,64.333c-12.774-2.411-22.118-6.874-26.893-12.134-1.061-1.169-1.895-2.377-2.492-3.61.679-1.395,1.679-2.781,3.015-4.139h0v-9.162l-16.38-3.092h0c-3.794,3.858-5.586,7.872-5.586,11.821l-.004.002v9.162c0,3.818,1.678,7.575,4.853,11.072,6.459,7.114,19.113,13.149,36.431,16.418l7.057-7.176v-9.162h0Z"/>
	<g>
	  <g id="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="20.724" y1="64.253" x2="20.724" y2="55.091"/>
	  </g>
	  <g id="LINE-2" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.318" y1="52.199" x2="20.724" y2="55.091"/>
	  </g>
	  <g id="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m64.211,64.333c-12.774-2.411-22.118-6.874-26.893-12.134"/>
	  </g>
	  <g id="LINE-3" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="64.212" y1="64.333" x2="64.211" y2="64.333"/>
	  </g>
	  <g id="LINE-4" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="64.212" y1="73.495" x2="64.212" y2="64.333"/>
	  </g>
	  <g id="LINE-5" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="57.155" y1="80.671" x2="64.212" y2="73.495"/>
	  </g>
	  <g id="LINE-6" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="57.155" y1="71.509" x2="64.212" y2="64.333"/>
	  </g>
	  <g id="LINE-7" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="57.155" y1="80.671" x2="57.155" y2="71.509"/>
	  </g>
	  <g id="SPLINE-2" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m20.724,64.253c6.459,7.114,19.113,13.149,36.431,16.418"/>
	  </g>
	  <g id="SPLINE-3" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m20.724,55.091c6.459,7.114,19.113,13.149,36.431,16.418"/>
	  </g>
	  <g id="SPLINE-4" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m34.826,48.591c.679-1.395,1.679-2.782,3.016-4.141"/>
	  </g>
	  <g id="SPLINE-5" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m37.318,52.199c-4.775-5.259-4.98-11.315.523-16.911"/>
	  </g>
	  <g id="LINE-8" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="20.724" y1="55.091" x2="37.318" y2="52.199"/>
	  </g>
	  <g id="LINE-9" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="20.724" y1="64.253" x2="20.724" y2="55.091"/>
	  </g>
	  <g id="SPLINE-6" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m15.871,53.181c0,3.818,1.678,7.575,4.853,11.072"/>
	  </g>
	  <g id="SPLINE-7" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m21.462,32.196c-7.461,7.587-7.197,15.781-.737,22.895"/>
	  </g>
	  <g id="LINE-10" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="21.462" y1="32.196" x2="21.462" y2="32.196"/>
	  </g>
	  <g id="LINE-11" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.842" y1="35.288" x2="21.462" y2="32.196"/>
	  </g>
	  <g id="LINE-12" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.842" y1="44.45" x2="37.842" y2="35.288"/>
	  </g>
	  <g id="LINE-13" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.842" y1="44.45" x2="37.842" y2="44.45"/>
	  </g>
	  <g id="LINE-14" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.842" y1="35.288" x2="37.842" y2="35.288"/>
	  </g>
	  <g id="LINE-15" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="15.871" y1="53.181" x2="15.871" y2="44.019"/>
	  </g>
	</g>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m71.268,47.996c-9.217-1.74-15.754-5.082-18.651-8.965.312-.415.668-.828,1.07-1.237v-9.162l-16.379-3.092c-2.859,2.908-4.21,5.933-4.211,8.91h-.003v9.162c0,8.733,11.648,17.045,31.117,20.72l7.057-7.176v-9.162Z"/>
	<g id="LINE-16" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="37.309" y1="25.54" x2="37.308" y2="25.54"/>
	</g>
	<g id="LINE-17" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="53.687" y1="28.632" x2="37.309" y2="25.54"/>
	</g>
	<g id="LINE-18" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="53.687" y1="37.794" x2="53.687" y2="28.632"/>
	</g>
	<g id="LINE-19" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="53.687" y1="37.794" x2="53.687" y2="37.794"/>
	</g>
	<g id="LINE-20" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="53.687" y1="28.632" x2="53.687" y2="28.632"/>
	</g>
	<g id="SPLINE-8" data-name="SPLINE">
	  <path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.616,39.032c.312-.416.669-.829,1.071-1.238"/>
	</g>
	<g id="SPLINE-9" data-name="SPLINE">
	  <path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m71.268,47.996c-17.033-3.215-24.919-11.901-17.581-19.363"/>
	</g>
	<g id="LINE-21" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="71.268" y1="47.996" x2="71.268" y2="47.996"/>
	</g>
	<g id="LINE-22" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="71.268" y1="57.157" x2="71.268" y2="47.996"/>
	</g>
	<g id="LINE-23" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="64.212" y1="64.333" x2="71.268" y2="57.157"/>
	</g>
	<g id="LINE-24" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="64.212" y1="55.172" x2="71.268" y2="47.996"/>
	</g>
	<g id="LINE-25" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="64.212" y1="64.333" x2="64.212" y2="55.172"/>
	</g>
	<g id="SPLINE-10" data-name="SPLINE">
	  <path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m33.095,43.613c0,8.733,11.648,17.045,31.117,20.72"/>
	</g>
	<g id="SPLINE-11" data-name="SPLINE">
	  <path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m37.308,25.54c-11.247,11.437.797,24.704,26.903,29.631"/>
	</g>
	<g id="LINE-26" data-name="LINE">
	  <line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="33.095" y1="43.613" x2="33.095" y2="34.452"/>
	</g>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.198,32.624c.034-.117.071-.234.111-.351-.04.117-.078.234-.111.351Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.745,29.703c.066-.085.137-.17.207-.254-.07.085-.141.169-.207.254Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.226,30.426c.059-.091.123-.182.186-.273-.064.091-.127.182-.186.273Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.475,30.063c.062-.087.128-.173.195-.26-.066.087-.132.173-.195.26Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.454,31.889c.045-.11.093-.22.144-.33-.051.11-.099.22-.144.33Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52,30.79c.057-.097.118-.193.18-.29-.062.096-.122.193-.18.29Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.796,31.155c.054-.102.111-.203.17-.305-.059.102-.116.203-.17.305Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.103,32.992c.027-.12.059-.24.093-.36-.034.12-.065.24-.093.36Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.315,32.256c.04-.114.083-.228.129-.342-.046.114-.089.228-.129.342Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.614,31.522c.049-.106.103-.212.157-.317-.055.106-.108.211-.157.317Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m53.037,29.344c.073-.086.151-.173.228-.259-.077.086-.154.172-.228.259Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.103,32.994c-.112.488-.167.976-.167,1.462h0c0-.486.055-.974.167-1.462Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m53.351,28.987c.108-.118.22-.237.336-.354-.116.118-.228.236-.336.354Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.444,31.914c.003-.008.006-.017.01-.025-.003.008-.006.017-.01.025Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.196,32.632s.001-.005.002-.008c0,.003-.001.005-.002.008Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.31,32.273c.002-.006.004-.011.005-.017-.002.006-.004.011-.005.017Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.103,32.994s0-.001,0-.002c0,0,0,.001,0,.002Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m53.265,29.085c.029-.033.057-.066.087-.098-.03.033-.057.065-.087.098Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m71.268,47.996l13.308-13.533-30.888-5.83c-.116.118-.228.236-.336.354-.03.033-.057.065-.087.098-.077.086-.155.172-.228.259-.029.035-.056.07-.085.104-.07.085-.141.169-.207.254-.026.033-.05.067-.076.101-.066.087-.132.173-.195.26-.021.03-.041.06-.062.09-.064.091-.127.182-.186.273-.016.025-.031.05-.047.075-.062.096-.123.193-.18.29-.012.02-.022.04-.034.06-.059.102-.116.203-.17.305-.009.016-.016.033-.025.049-.055.106-.108.211-.157.317-.006.012-.011.025-.016.037-.051.11-.099.22-.144.33-.003.008-.006.017-.01.025-.046.114-.089.228-.129.342-.002.006-.004.011-.005.017-.04.117-.078.234-.111.351,0,.003-.001.005-.002.008-.034.12-.065.24-.093.36,0,0,0,.001,0,.002-.112.488-.167.976-.167,1.462h0c0,5.707,7.61,11.138,20.332,13.54Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.952,29.448c.029-.035.056-.07.085-.104-.029.035-.056.07-.085.104Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.966,30.85c.012-.02.022-.04.034-.06-.012.02-.022.04-.034.06Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.597,31.559c.006-.012.011-.025.016-.037-.006.012-.011.025-.016.037Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m51.771,31.204c.008-.016.016-.033.025-.049-.009.016-.016.033-.025.049Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.669,29.803c.026-.033.049-.067.076-.101-.026.033-.05.067-.076.101Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.413,30.153c.021-.03.04-.06.062-.09-.021.03-.041.06-.062.09Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m52.18,30.501c.016-.025.031-.05.047-.075-.016.025-.031.05-.047.075Z"/>
	<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m53.673,19.327c-1.869,1.9-2.752,3.878-2.751,5.823h-.002v9.162c0,5.707,7.612,11.138,20.334,13.54l13.308-13.533v-9.162l-30.888-5.83Z"/>
	<g>
	  <g id="LINE-27" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="71.253" y1="47.852" x2="84.561" y2="34.319"/>
	  </g>
	  <g id="LINE-28" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="84.561" y1="34.319" x2="84.561" y2="25.158"/>
	  </g>
	  <g id="LINE-29" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="71.253" y1="38.69" x2="84.561" y2="25.158"/>
	  </g>
	  <g id="LINE-30" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="71.253" y1="47.852" x2="71.253" y2="38.69"/>
	  </g>
	  <g id="SPLINE-12" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m50.919,34.312c0,5.707,7.612,11.138,20.334,13.54"/>
	  </g>
	  <g id="SPLINE-13" data-name="SPLINE">
		<path style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} d="m53.673,19.327c-7.35,7.474.521,16.143,17.58,19.363"/>
	  </g>
	  <g id="LINE-31" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="84.561" y1="25.158" x2="53.673" y2="19.327"/>
	  </g>
	  <g id="LINE-32" data-name="LINE">
		<line style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} x1="50.919" y1="34.312" x2="50.919" y2="25.151"/>
	  </g>
	</g>
  </svg>;

	const CornerSVG : React.FunctionComponent = () => <svg id="Vrstva_1" style={{ verticalAlign: 'middle', marginRight: '5px' }} data-name="Vrstva 1" xmlns="http://www.w3.org/2000/svg" width="40px" height="40px"  viewBox="0 0 99.221 99.221">
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="9.342 47.517 8.007 39.853 49.094 68.732 49.3 76.684 9.342 47.517"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="9.342 47.517 8.007 39.853 49.094 68.732 49.3 76.684 9.342 47.517"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="49.094 68.732 8.007 39.855 29.577 28.713 49.999 41.125 70.758 28.719 91.214 39.448 49.094 68.732"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="49.094 68.732 8.007 39.855 29.577 28.713 49.999 41.125 70.758 28.719 91.214 39.448 49.094 68.732"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="49.094 68.732 91.214 39.448 90.208 47.104 49.3 76.684 49.094 68.732"/>
		<polygon style={{fill: '#fff', stroke: '#299b49', strokeWidth: '4px', strokeLinejoin: 'round'}} points="49.094 68.732 91.214 39.448 90.208 47.104 49.3 76.684 49.094 68.732"/>
	</svg>


	const StaircasesSVGComponent: React.FunctionComponent<{selection: {[prop: string]: boolean}, setSelection : (selection: {[prop: string]: boolean}) => void, radiusType: string}> = ({selection, setSelection, radiusType}) => 
	{
		const isAvailable = (stage: string) => StaircaseStepsConfiguratorUtils.determineRadiusStageAvailability(stage, radiusType, selection);
		const isActive = (stage: string) => selection[stage];
		const handler = (stage: string) => {

			if(isAvailable(stage))
			{
				if(isActive(stage))
				{
					setSelection(Object.fromEntries(Object.entries(selection).map(entry => [
						entry[0],
						entry[0] === stage ? false : entry[1]
					])));
				}
				else 
				{
					setSelection(Object.fromEntries(Object.entries(selection).map(entry => [
						entry[0],
						entry[0] === stage ? true : entry[1]
					])));
				}
			}

			window.focus();
		};

		return <>
			<svg xmlns="http://www.w3.org/2000/svg" width="203.319mm" height="188.988mm" viewBox="0 0 576.336 535.715">

			<text x={585} y={70} className={classNames(radiusType == '0' ? 'hidden' : '', isActive('S') ? 'active' : '', isAvailable('S') ? 'available' : '')}>S</text>
			<text x={585} y={25} className={classNames(radiusType == 'S' ? 'hidden' : '', isActive('0') ? 'active' : '', isAvailable('0') ? 'available' : '')}>0</text>
			<text x={565} y={110} className={classNames(isActive('1') ? 'active' : '', isAvailable('1') ? 'available' : '')}>1</text>
			<text x={545} y={155} className={classNames(isActive('2') ? 'active' : '', isAvailable('2') ? 'available' : '')}>2</text>
			<text x={525} y={200} className={classNames(isActive('3') ? 'active' : '', isAvailable('3') ? 'available' : '')}>3</text>
			<text x={505} y={245} className={classNames(isActive('4') ? 'active' : '', isAvailable('4') ? 'available' : '')}>4</text>
			<text x={485} y={290} className={classNames(isActive('5') ? 'active' : '', isAvailable('5') ? 'available' : '')}>5</text>
			<text x={465} y={335} className={classNames(isActive('6') ? 'active' : '', isAvailable('6') ? 'available' : '')}>6</text>
			<text x={445} y={380} className={classNames(isActive('7') ? 'active' : '', isAvailable('7') ? 'available' : '')}>7</text>
			<text x={425} y={425} className={classNames(isActive('8') ? 'active' : '', isAvailable('8') ? 'available' : '')}>8</text>
			<text x={405} y={470} className={classNames(isActive('9') ? 'active' : '', isAvailable('9') ? 'available' : '')}>9</text>
			<text x={385} y={515} className={classNames(isActive('10') ? 'active' : '', isAvailable('10') ? 'available' : '')}>10</text>
			<g id="R10" onClick={() => handler('10')} className={classNames(isActive('10') ? 'active' : '', isAvailable('10') ? 'available' : '')}>
				<path className="cls-4" d="m361.495,489.318c-63.81-12.045-119.196-29.123-164.841-49.709-47.381-21.37-84.263-46.52-109.067-73.784-20.66-22.71-32.758-46.688-35.897-70.953,3.325-25.53,16.687-51.227,41.088-76.04h0v-25.482l-45.924-8.669C17.743,214.283,2.909,245.001.942,275.371l-.234-.019v32.479c0,31.593,13.88,62.685,40.192,91.611,27.215,29.919,67.723,57.512,119.8,80.961,50.106,22.561,110.92,41.286,181.01,54.517.158.03.316.057.474.086l19.787-20.121v-25.481c-.158-.03-.318-.057-.476-.087Z"/>
				<g>
				<g id="LINE">
					<line className="cls-2" x1="342.184" y1="535.006" x2="361.97" y2="514.886"/>
				</g>
				<g id="LINE-2" data-name="LINE">
					<line className="cls-2" x1="361.97" y1="514.886" x2="361.97" y2="489.404"/>
				</g>
				<g id="LINE-3" data-name="LINE">
					<line className="cls-2" x1="342.184" y1="509.525" x2="361.97" y2="489.404"/>
				</g>
				<g id="LINE-4" data-name="LINE">
					<line className="cls-2" x1="342.184" y1="535.006" x2="342.184" y2="509.525"/>
				</g>
				<g id="SPLINE">
					<path className="cls-2" d="m160.7,480.403c50.106,22.561,110.92,41.286,181.01,54.517.158.03.316.057.474.086"/>
				</g>
				<g id="SPLINE-2" data-name="SPLINE">
					<path className="cls-2" d="m160.7,454.922c50.106,22.561,110.92,41.286,181.01,54.517.158.03.316.057.474.086"/>
				</g>
				<g id="LINE-5" data-name="LINE">
					<line className="cls-2" x1="160.7" y1="480.403" x2="160.7" y2="454.922"/>
				</g>
				<g id="LINE-6" data-name="LINE">
					<line className="cls-2" x1="196.654" y1="439.609" x2="160.7" y2="454.922"/>
				</g>
				<g id="SPLINE-3" data-name="SPLINE">
					<path className="cls-2" d="m361.97,489.404c-.158-.03-.318-.057-.476-.087-63.81-12.045-119.196-29.123-164.841-49.709"/>
				</g>
				<g id="LINE-7" data-name="LINE">
					<line className="cls-2" x1="160.7" y1="454.922" x2="196.654" y2="439.609"/>
				</g>
				<g id="LINE-8" data-name="LINE">
					<line className="cls-2" x1="160.7" y1="480.403" x2="160.7" y2="454.922"/>
				</g>
				<g id="SPLINE-4" data-name="SPLINE">
					<path className="cls-2" d="m40.9,399.442c27.215,29.919,67.723,57.512,119.8,80.961"/>
				</g>
				<g id="SPLINE-5" data-name="SPLINE">
					<path className="cls-2" d="m40.9,373.961c27.215,29.919,67.723,57.512,119.8,80.961"/>
				</g>
				<g id="LINE-9" data-name="LINE">
					<line className="cls-2" x1="40.9" y1="399.442" x2="40.9" y2="373.961"/>
				</g>
				<g id="LINE-10" data-name="LINE">
					<line className="cls-2" x1="87.586" y1="365.825" x2="40.9" y2="373.961"/>
				</g>
				<g id="SPLINE-6" data-name="SPLINE">
					<path className="cls-2" d="m196.654,439.609c-47.381-21.369-84.263-46.52-109.067-73.784"/>
				</g>
				<g id="LINE-11" data-name="LINE">
					<line className="cls-2" x1="50.898" y1="284.948" x2="50.898" y2="279.378"/>
				</g>
				<g id="ELLIPSE">
					<path className="cls-2" d="m87.586,365.825c-25.049-27.535-37.525-56.932-36.688-86.448"/>
				</g>
				<g id="LINE-12" data-name="LINE">
					<line className="cls-2" x1="40.9" y1="373.961" x2="87.586" y2="365.825"/>
				</g>
				<g id="LINE-13" data-name="LINE">
					<line className="cls-2" x1="40.9" y1="399.442" x2="40.9" y2="373.961"/>
				</g>
				<g id="SPLINE-7" data-name="SPLINE">
					<path className="cls-2" d="m.709,307.831c0,31.593,13.88,62.685,40.192,91.611"/>
				</g>
				<g id="SPLINE-8" data-name="SPLINE">
					<path className="cls-2" d="m.759,279.068c-.996,32.72,12.919,64.966,40.142,94.894"/>
				</g>
				<g id="LINE-14" data-name="LINE">
					<line className="cls-2" x1="50.898" y1="279.378" x2=".759" y2="279.068"/>
				</g>
				<g id="LINE-15" data-name="LINE">
					<line className="cls-2" x1=".709" y1="307.831" x2=".709" y2="282.35"/>
				</g>
				<g id="SPLINE-9" data-name="SPLINE">
					<path className="cls-2" d="m51.684,294.905c3.317-25.541,16.681-51.25,41.093-76.074"/>
				</g>
				<g id="SPLINE-10" data-name="SPLINE">
					<path className="cls-2" d="m50.898,279.378c.815-28.793,14.293-57.975,41.879-86.027"/>
				</g>
				<g id="LINE-16" data-name="LINE">
					<line className="cls-2" x1="50.898" y1="284.948" x2="50.898" y2="279.378"/>
				</g>
				<g id="LINE-17" data-name="LINE">
					<line className="cls-2" x1=".759" y1="279.068" x2="50.898" y2="279.378"/>
				</g>
				<g id="SPLINE-11" data-name="SPLINE">
					<path className="cls-2" d="m46.853,184.681C16.561,215.484,1.72,247.497.759,279.068"/>
				</g>
				<g id="LINE-18" data-name="LINE">
					<line className="cls-2" x1="92.778" y1="193.35" x2="46.853" y2="184.681"/>
				</g>
				<g id="LINE-19" data-name="LINE">
					<line className="cls-2" x1="92.778" y1="218.831" x2="92.778" y2="193.35"/>
				</g>
				<g id="LINE-20" data-name="LINE">
					<line className="cls-2" x1="92.777" y1="218.831" x2="92.778" y2="218.831"/>
				</g>
				<g id="LINE-21" data-name="LINE">
					<line className="cls-2" x1="92.777" y1="193.35" x2="92.778" y2="193.35"/>
				</g>
				</g>
			</g>
			<g id="R09" onClick={() => handler('9')} className={classNames(isActive('9') ? 'active' : '', isAvailable('9') ? 'available' : '')}>
				<path className="cls-4" d="m381.121,443.876c-78.107-14.743-142.373-37.765-189.787-65.719-49.185-28.998-80.173-63.309-89.684-99.2-.96-3.623-1.688-7.262-2.206-10.914,3.275-22.824,15.389-45.778,37.2-67.957h0v-25.482l-45.558-8.599h0c-28.72,29.205-42.281,59.593-42.284,89.494l-.011.01v25.481c0,8.693,1.147,17.344,3.402,25.913,10.458,39.751,44.764,77.706,99.204,109.757,52.46,30.886,123.593,56.328,210.099,72.656l.474.086,19.628-19.96v-25.481c-.158-.03-.318-.057-.476-.087Z"/>
				<g>
				<g id="LINE-22" data-name="LINE">
					<line className="cls-2" x1="151.396" y1="416.661" x2="151.396" y2="391.18"/>
				</g>
				<g id="LINE-23" data-name="LINE">
					<line className="cls-2" x1="191.334" y1="378.158" x2="151.396" y2="391.18"/>
				</g>
				<g id="SPLINE-12" data-name="SPLINE">
					<path className="cls-2" d="m381.597,443.963c-.158-.03-.318-.057-.476-.087-78.107-14.743-142.373-37.765-189.787-65.719"/>
				</g>
				<g id="LINE-24" data-name="LINE">
					<line className="cls-2" x1="381.597" y1="469.444" x2="381.597" y2="443.963"/>
				</g>
				<g id="LINE-25" data-name="LINE">
					<line className="cls-2" x1="361.968" y1="489.404" x2="381.597" y2="469.444"/>
				</g>
				<g id="LINE-26" data-name="LINE">
					<line className="cls-2" x1="361.968" y1="463.923" x2="381.597" y2="443.963"/>
				</g>
				<g id="LINE-27" data-name="LINE">
					<line className="cls-2" x1="361.968" y1="489.404" x2="361.968" y2="463.923"/>
				</g>
				<g id="SPLINE-13" data-name="SPLINE">
					<path className="cls-2" d="m361.495,489.318c.158.03.316.057.474.086"/>
				</g>
				<g id="SPLINE-14" data-name="SPLINE">
					<path className="cls-2" d="m361.495,463.837c.158.03.316.057.474.086"/>
				</g>
				<g id="SPLINE-15" data-name="SPLINE">
					<path className="cls-2" d="m151.396,416.661c52.46,30.886,123.593,56.328,210.099,72.656"/>
				</g>
				<g id="SPLINE-16" data-name="SPLINE">
					<path className="cls-2" d="m151.396,391.18c52.46,30.886,123.593,56.328,210.099,72.656"/>
				</g>
				<g id="LINE-28" data-name="LINE">
					<line className="cls-2" x1="52.192" y1="306.905" x2="52.192" y2="281.423"/>
				</g>
				<g id="LINE-29" data-name="LINE">
					<line className="cls-2" x1="101.65" y1="278.958" x2="52.192" y2="281.423"/>
				</g>
				<g id="SPLINE-17" data-name="SPLINE">
					<path className="cls-2" d="m191.334,378.158c-49.185-28.998-80.173-63.309-89.684-99.2"/>
				</g>
				<g id="LINE-30" data-name="LINE">
					<line className="cls-2" x1="151.396" y1="391.18" x2="191.334" y2="378.158"/>
				</g>
				<g id="LINE-31" data-name="LINE">
					<line className="cls-2" x1="151.396" y1="416.661" x2="151.396" y2="391.18"/>
				</g>
				<g id="ELLIPSE-2" data-name="ELLIPSE">
					<path className="cls-2" d="m52.192,306.905c10.458,39.751,44.764,77.706,99.204,109.757"/>
				</g>
				<g id="ELLIPSE-3" data-name="ELLIPSE">
					<path className="cls-2" d="m52.192,281.423c10.458,39.751,44.764,77.706,99.204,109.757"/>
				</g>
				<g id="SPLINE-18" data-name="SPLINE">
					<path className="cls-2" d="m99.436,268.091c3.265-22.839,15.382-45.81,37.207-68.003"/>
				</g>
				<g id="SPLINE-19" data-name="SPLINE">
					<path className="cls-2" d="m101.65,278.958c-9.123-34.425,1.51-70.303,34.993-104.352"/>
				</g>
				<g id="LINE-32" data-name="LINE">
					<line className="cls-2" x1="52.192" y1="281.423" x2="101.65" y2="278.958"/>
				</g>
				<g id="LINE-33" data-name="LINE">
					<line className="cls-2" x1="52.192" y1="306.905" x2="52.192" y2="281.423"/>
				</g>
				<g id="SPLINE-20" data-name="SPLINE">
					<path className="cls-2" d="m48.79,280.991c0,8.693,1.147,17.344,3.402,25.913"/>
				</g>
				<g id="SPLINE-21" data-name="SPLINE">
					<path className="cls-2" d="m91.085,166.006c-37.073,37.699-48.906,77.369-38.894,115.417"/>
				</g>
				<g id="LINE-34" data-name="LINE">
					<line className="cls-2" x1="91.086" y1="166.006" x2="91.085" y2="166.006"/>
				</g>
				<g id="LINE-35" data-name="LINE">
					<line className="cls-2" x1="136.643" y1="174.606" x2="91.086" y2="166.006"/>
				</g>
				<g id="LINE-36" data-name="LINE">
					<line className="cls-2" x1="136.643" y1="200.087" x2="136.643" y2="174.606"/>
				</g>
				<g id="LINE-37" data-name="LINE">
					<line className="cls-2" x1="136.643" y1="200.087" x2="136.643" y2="200.087"/>
				</g>
				<g id="LINE-38" data-name="LINE">
					<line className="cls-2" x1="136.643" y1="174.606" x2="136.643" y2="174.606"/>
				</g>
				<g id="LINE-39" data-name="LINE">
					<line className="cls-2" x1="48.79" y1="280.991" x2="48.79" y2="255.51"/>
				</g>
				</g>
			</g>
			<g id="R08" onClick={() => handler('8')} className={classNames(isActive('8') ? 'active' : '', isAvailable('8') ? 'available' : '')}>
				<path className="cls-4" d="m401.223,398.524c-52.376-9.886-97.795-23.924-135.222-40.835-38.412-17.356-68.734-37.896-89.17-60.405-16.176-17.818-25.91-36.604-28.916-55.651,3.195-20.15,14.03-40.397,33.28-59.972h0v-25.482l-45.558-8.6h0c-25.245,25.672-37.627,52.351-38.45,78.663-.029.938-.041,1.875-.041,2.812l-.003.003v25.481c0,26.308,11.538,52.199,33.413,76.292,22.872,25.19,56.795,48.152,99.771,67.534,41.85,18.875,92.675,34.539,151.271,45.6l19.627-19.958v-25.481h-.002Z"/>
				<g>
				<g id="SPLINE-22" data-name="SPLINE">
					<path className="cls-2" d="m401.222,398.524c-52.376-9.886-97.795-23.924-135.222-40.835"/>
				</g>
				<g id="LINE-40" data-name="LINE">
					<line className="cls-2" x1="401.223" y1="398.524" x2="401.222" y2="398.524"/>
				</g>
				<g id="LINE-41" data-name="LINE">
					<line className="cls-2" x1="401.223" y1="424.005" x2="401.223" y2="398.524"/>
				</g>
				<g id="LINE-42" data-name="LINE">
					<line className="cls-2" x1="381.597" y1="443.963" x2="401.223" y2="424.005"/>
				</g>
				<g id="LINE-43" data-name="LINE">
					<line className="cls-2" x1="381.597" y1="418.482" x2="401.223" y2="398.524"/>
				</g>
				<g id="LINE-44" data-name="LINE">
					<line className="cls-2" x1="381.597" y1="443.963" x2="381.597" y2="418.482"/>
				</g>
				<g id="SPLINE-23" data-name="SPLINE">
					<path className="cls-2" d="m230.326,398.364c41.85,18.875,92.675,34.539,151.271,45.6"/>
				</g>
				<g id="SPLINE-24" data-name="SPLINE">
					<path className="cls-2" d="m230.326,372.882c41.85,18.875,92.675,34.539,151.271,45.6"/>
				</g>
				<g id="LINE-45" data-name="LINE">
					<line className="cls-2" x1="230.326" y1="398.364" x2="230.326" y2="372.882"/>
				</g>
				<g id="LINE-46" data-name="LINE">
					<line className="cls-2" x1="265.999" y1="357.688" x2="230.326" y2="372.882"/>
				</g>
				<g id="LINE-47" data-name="LINE">
					<line className="cls-2" x1="130.555" y1="330.829" x2="130.555" y2="305.348"/>
				</g>
				<g id="LINE-48" data-name="LINE">
					<line className="cls-2" x1="176.83" y1="297.284" x2="130.555" y2="305.348"/>
				</g>
				<g id="ELLIPSE-4" data-name="ELLIPSE">
					<path className="cls-2" d="m265.999,357.688c-38.412-17.356-68.734-37.896-89.17-60.405"/>
				</g>
				<g id="LINE-49" data-name="LINE">
					<line className="cls-2" x1="230.326" y1="372.882" x2="265.999" y2="357.688"/>
				</g>
				<g id="LINE-50" data-name="LINE">
					<line className="cls-2" x1="230.326" y1="398.364" x2="230.326" y2="372.882"/>
				</g>
				<g id="ELLIPSE-5" data-name="ELLIPSE">
					<path className="cls-2" d="m130.555,330.829c22.872,25.19,56.795,48.152,99.771,67.534"/>
				</g>
				<g id="ELLIPSE-6" data-name="ELLIPSE">
					<path className="cls-2" d="m130.555,305.348c22.872,25.19,56.795,48.152,99.771,67.534"/>
				</g>
				<g id="LINE-51" data-name="LINE">
					<line className="cls-2" x1="146.938" y1="231.271" x2="146.938" y2="226.549"/>
				</g>
				<g id="ELLIPSE-7" data-name="ELLIPSE">
					<path className="cls-2" d="m176.83,297.284c-20.435-22.509-30.599-46.561-29.892-70.735"/>
				</g>
				<g id="LINE-52" data-name="LINE">
					<line className="cls-2" x1="130.555" y1="305.348" x2="176.83" y2="297.284"/>
				</g>
				<g id="LINE-53" data-name="LINE">
					<line className="cls-2" x1="130.555" y1="330.829" x2="130.555" y2="305.348"/>
				</g>
				<g id="SPLINE-25" data-name="SPLINE">
					<path className="cls-2" d="m97.142,254.537c0,26.308,11.538,52.199,33.413,76.292"/>
				</g>
				<g id="SPLINE-26" data-name="SPLINE">
					<path className="cls-2" d="m97.186,226.241c-.853,27.274,10.714,54.154,33.369,79.107"/>
				</g>
				<g id="LINE-54" data-name="LINE">
					<line className="cls-2" x1="146.938" y1="226.549" x2="97.186" y2="226.241"/>
				</g>
				<g id="LINE-55" data-name="LINE">
					<line className="cls-2" x1="97.142" y1="254.537" x2="97.142" y2="229.056"/>
				</g>
				<g id="SPLINE-27" data-name="SPLINE">
					<path className="cls-2" d="m147.91,241.653c3.191-20.156,14.027-40.411,33.284-59.993"/>
				</g>
				<g id="SPLINE-28" data-name="SPLINE">
					<path className="cls-2" d="m146.938,226.549c.688-23.554,11.69-47.424,34.256-70.37"/>
				</g>
				<g id="LINE-56" data-name="LINE">
					<line className="cls-2" x1="146.938" y1="231.271" x2="146.938" y2="226.549"/>
				</g>
				<g id="LINE-57" data-name="LINE">
					<line className="cls-2" x1="97.186" y1="226.241" x2="146.938" y2="226.549"/>
				</g>
				<g id="SPLINE-29" data-name="SPLINE">
					<path className="cls-2" d="m135.636,147.579c-25.246,25.672-37.627,52.351-38.45,78.663"/>
				</g>
				<g id="LINE-58" data-name="LINE">
					<line className="cls-2" x1="135.636" y1="147.579" x2="135.636" y2="147.579"/>
				</g>
				<g id="LINE-59" data-name="LINE">
					<line className="cls-2" x1="181.194" y1="156.178" x2="135.636" y2="147.579"/>
				</g>
				<g id="LINE-60" data-name="LINE">
					<line className="cls-2" x1="181.194" y1="181.659" x2="181.194" y2="156.178"/>
				</g>
				<g id="LINE-61" data-name="LINE">
					<line className="cls-2" x1="181.194" y1="181.66" x2="181.194" y2="181.659"/>
				</g>
				<g id="LINE-62" data-name="LINE">
					<line className="cls-2" x1="181.194" y1="156.179" x2="181.194" y2="156.178"/>
				</g>
				</g>
			</g>
			<g id="R07" onClick={() => handler('7')} className={classNames(isActive('7') ? 'active' : '', isAvailable('7') ? 'available' : '')}>
				<path className="cls-4" d="m420.851,353.084c-62.604-11.817-114.013-30.305-151.966-52.726-39.242-23.181-64.003-50.607-71.595-79.301-.54-2.039-.982-4.085-1.347-6.135,3.123-17.402,12.698-34.869,29.323-51.775h0v-25.482l-45.555-8.599h0c-23.51,23.907-34.607,48.778-34.609,73.25l-.009.007v25.481c0,7.11.937,14.187,2.78,21.197,8.549,32.523,36.596,63.59,81.114,89.847,42.995,25.36,101.252,46.276,172.237,59.675l19.627-19.958v-25.481h-.002Z"/>
				<g>
				<g id="LINE-63" data-name="LINE">
					<line className="cls-2" x1="228.987" y1="338.849" x2="228.987" y2="313.367"/>
				</g>
				<g id="LINE-64" data-name="LINE">
					<line className="cls-2" x1="268.883" y1="300.358" x2="228.987" y2="313.367"/>
				</g>
				<g id="SPLINE-30" data-name="SPLINE">
					<path className="cls-2" d="m420.849,353.084c-62.604-11.817-114.013-30.305-151.965-52.726"/>
				</g>
				<g id="LINE-65" data-name="LINE">
					<line className="cls-2" x1="420.851" y1="353.084" x2="420.849" y2="353.084"/>
				</g>
				<g id="LINE-66" data-name="LINE">
					<line className="cls-2" x1="420.851" y1="378.565" x2="420.851" y2="353.084"/>
				</g>
				<g id="LINE-67" data-name="LINE">
					<line className="cls-2" x1="401.223" y1="398.524" x2="420.851" y2="378.565"/>
				</g>
				<g id="LINE-68" data-name="LINE">
					<line className="cls-2" x1="401.223" y1="373.043" x2="420.851" y2="353.084"/>
				</g>
				<g id="LINE-69" data-name="LINE">
					<line className="cls-2" x1="401.223" y1="398.524" x2="401.223" y2="373.043"/>
				</g>
				<g id="SPLINE-31" data-name="SPLINE">
					<path className="cls-2" d="m228.987,338.849c42.995,25.36,101.252,46.276,172.237,59.675"/>
				</g>
				<g id="SPLINE-32" data-name="SPLINE">
					<path className="cls-2" d="m228.987,313.367c42.995,25.36,101.252,46.276,172.237,59.675"/>
				</g>
				<g id="ELLIPSE-8" data-name="ELLIPSE">
					<path className="cls-2" d="m268.883,300.358c-39.242-23.181-64.003-50.607-71.595-79.301"/>
				</g>
				<g id="LINE-70" data-name="LINE">
					<line className="cls-2" x1="228.987" y1="313.367" x2="268.883" y2="300.358"/>
				</g>
				<g id="LINE-71" data-name="LINE">
					<line className="cls-2" x1="228.987" y1="338.849" x2="228.987" y2="313.367"/>
				</g>
				<g id="ELLIPSE-9" data-name="ELLIPSE">
					<path className="cls-2" d="m147.873,249.002c8.549,32.523,36.596,63.59,81.114,89.847"/>
				</g>
				<g id="ELLIPSE-10" data-name="ELLIPSE">
					<path className="cls-2" d="m147.873,223.521c8.549,32.523,36.596,63.59,81.114,89.847"/>
				</g>
				<g id="LINE-72" data-name="LINE">
					<line className="cls-2" x1="147.873" y1="249.002" x2="147.873" y2="223.521"/>
				</g>
				<g id="LINE-73" data-name="LINE">
					<line className="cls-2" x1="197.288" y1="221.057" x2="147.873" y2="223.521"/>
				</g>
				<g id="SPLINE-33" data-name="SPLINE">
					<path className="cls-2" d="m195.935,214.951c3.118-17.412,12.695-34.889,29.329-51.804"/>
				</g>
				<g id="SPLINE-34" data-name="SPLINE">
					<path className="cls-2" d="m197.288,221.057c-7.28-27.51,1.215-56.178,27.977-83.391"/>
				</g>
				<g id="LINE-74" data-name="LINE">
					<line className="cls-2" x1="147.873" y1="223.521" x2="197.288" y2="221.057"/>
				</g>
				<g id="LINE-75" data-name="LINE">
					<line className="cls-2" x1="147.873" y1="249.002" x2="147.873" y2="223.521"/>
				</g>
				<g id="SPLINE-35" data-name="SPLINE">
					<path className="cls-2" d="m145.093,227.805c0,7.11.937,14.187,2.78,21.197"/>
				</g>
				<g id="SPLINE-36" data-name="SPLINE">
					<path className="cls-2" d="m179.711,129.067c-30.343,30.855-40.024,63.317-31.838,94.454"/>
				</g>
				<g id="LINE-76" data-name="LINE">
					<line className="cls-2" x1="179.711" y1="129.067" x2="179.711" y2="129.067"/>
				</g>
				<g id="LINE-77" data-name="LINE">
					<line className="cls-2" x1="225.265" y1="137.665" x2="179.711" y2="129.067"/>
				</g>
				<g id="LINE-78" data-name="LINE">
					<line className="cls-2" x1="225.265" y1="163.147" x2="225.265" y2="137.665"/>
				</g>
				<g id="LINE-79" data-name="LINE">
					<line className="cls-2" x1="225.265" y1="163.147" x2="225.265" y2="163.147"/>
				</g>
				<g id="LINE-80" data-name="LINE">
					<line className="cls-2" x1="225.265" y1="137.666" x2="225.265" y2="137.665"/>
				</g>
				<g id="LINE-81" data-name="LINE">
					<line className="cls-2" x1="145.093" y1="227.805" x2="145.093" y2="202.324"/>
				</g>
				</g>
			</g>
			<g id="R06" onClick={() => handler('6')} className={classNames(isActive('6') ? 'active' : '', isAvailable('6') ? 'available' : '')}>
				<path className="cls-4" d="m440.476,307.644c-40.736-7.689-76.062-18.607-105.172-31.761-29.876-13.499-53.459-29.474-69.355-46.981-11.783-12.978-19.164-26.617-21.977-40.464,3.012-14.732,11.3-29.498,25.368-43.803h0v-25.482l-45.555-8.599h0c-20.227,20.569-30.114,41.944-30.774,63.025h0s-.036,0-.036,0v27.744c0,20.915,9.079,41.667,26.738,61.118,18.304,20.159,45.49,38.553,79.96,54.099,33.531,15.123,74.228,27.683,121.176,36.544l19.627-19.959v-25.481h-.002Z"/>
				<g>
				<g id="SPLINE-37" data-name="SPLINE">
					<path className="cls-2" d="m440.476,307.644c-40.736-7.689-76.063-18.607-105.172-31.761"/>
				</g>
				<g id="LINE-82" data-name="LINE">
					<line className="cls-2" x1="440.478" y1="307.644" x2="440.476" y2="307.644"/>
				</g>
				<g id="LINE-83" data-name="LINE">
					<line className="cls-2" x1="440.478" y1="333.125" x2="440.478" y2="307.644"/>
				</g>
				<g id="LINE-84" data-name="LINE">
					<line className="cls-2" x1="420.851" y1="353.084" x2="440.478" y2="333.125"/>
				</g>
				<g id="LINE-85" data-name="LINE">
					<line className="cls-2" x1="420.851" y1="327.603" x2="440.478" y2="307.644"/>
				</g>
				<g id="LINE-86" data-name="LINE">
					<line className="cls-2" x1="420.851" y1="353.084" x2="420.851" y2="327.603"/>
				</g>
				<g id="SPLINE-38" data-name="SPLINE">
					<path className="cls-2" d="m299.675,316.54c33.531,15.123,74.228,27.683,121.176,36.544"/>
				</g>
				<g id="SPLINE-39" data-name="SPLINE">
					<path className="cls-2" d="m299.675,291.059c33.531,15.123,74.228,27.683,121.176,36.544"/>
				</g>
				<g id="LINE-87" data-name="LINE">
					<line className="cls-2" x1="299.675" y1="316.54" x2="299.675" y2="291.059"/>
				</g>
				<g id="LINE-88" data-name="LINE">
					<line className="cls-2" x1="335.304" y1="275.883" x2="299.675" y2="291.059"/>
				</g>
				<g id="LINE-89" data-name="LINE">
					<line className="cls-2" x1="219.714" y1="262.441" x2="219.714" y2="236.96"/>
				</g>
				<g id="LINE-90" data-name="LINE">
					<line className="cls-2" x1="265.949" y1="228.902" x2="219.714" y2="236.96"/>
				</g>
				<g id="ELLIPSE-11" data-name="ELLIPSE">
					<path className="cls-2" d="m335.304,275.883c-29.876-13.499-53.459-29.474-69.355-46.981"/>
				</g>
				<g id="LINE-91" data-name="LINE">
					<line className="cls-2" x1="299.675" y1="291.059" x2="335.304" y2="275.883"/>
				</g>
				<g id="LINE-92" data-name="LINE">
					<line className="cls-2" x1="299.675" y1="316.54" x2="299.675" y2="291.059"/>
				</g>
				<g id="ELLIPSE-12" data-name="ELLIPSE">
					<path className="cls-2" d="m219.714,262.441c18.304,20.159,45.49,38.553,79.96,54.099"/>
				</g>
				<g id="ELLIPSE-13" data-name="ELLIPSE">
					<path className="cls-2" d="m219.714,236.96c18.304,20.159,45.49,38.553,79.96,54.099"/>
				</g>
				<g id="LINE-93" data-name="LINE">
					<line className="cls-2" x1="242.698" y1="177.553" x2="242.698" y2="173.886"/>
				</g>
				<g id="ELLIPSE-14" data-name="ELLIPSE">
					<path className="cls-2" d="m265.949,228.902c-15.894-17.507-23.801-36.214-23.251-55.016"/>
				</g>
				<g id="LINE-94" data-name="LINE">
					<line className="cls-2" x1="219.714" y1="236.96" x2="265.949" y2="228.902"/>
				</g>
				<g id="LINE-95" data-name="LINE">
					<line className="cls-2" x1="219.714" y1="262.441" x2="219.714" y2="236.96"/>
				</g>
				<g id="ELLIPSE-15" data-name="ELLIPSE">
					<path className="cls-2" d="m192.976,201.323c0,20.915,9.079,41.667,26.738,61.118"/>
				</g>
				<g id="ELLIPSE-16" data-name="ELLIPSE">
					<path className="cls-2" d="m193.012,173.579c-.679,21.672,8.4,43.221,26.703,63.38"/>
				</g>
				<g id="LINE-96" data-name="LINE">
					<line className="cls-2" x1="242.698" y1="173.886" x2="193.012" y2="173.579"/>
				</g>
				<g id="LINE-97" data-name="LINE">
					<line className="cls-2" x1="192.976" y1="201.323" x2="192.976" y2="175.842"/>
				</g>
				<g id="SPLINE-40" data-name="SPLINE">
					<path className="cls-2" d="m243.966,188.465c3.007-14.741,11.298-29.516,25.374-43.83"/>
				</g>
				<g id="SPLINE-41" data-name="SPLINE">
					<path className="cls-2" d="m242.698,173.886c.534-18.32,9.092-36.885,26.643-54.733"/>
				</g>
				<g id="LINE-98" data-name="LINE">
					<line className="cls-2" x1="242.698" y1="177.553" x2="242.698" y2="173.886"/>
				</g>
				<g id="LINE-99" data-name="LINE">
					<line className="cls-2" x1="193.012" y1="173.579" x2="242.698" y2="173.886"/>
				</g>
				<g id="SPLINE-42" data-name="SPLINE">
					<path className="cls-2" d="m223.786,110.554c-20.227,20.569-30.114,41.943-30.774,63.025"/>
				</g>
				<g id="LINE-100" data-name="LINE">
					<line className="cls-2" x1="223.786" y1="110.554" x2="223.786" y2="110.554"/>
				</g>
				<g id="LINE-101" data-name="LINE">
					<line className="cls-2" x1="269.341" y1="119.153" x2="223.786" y2="110.554"/>
				</g>
				<g id="LINE-102" data-name="LINE">
					<line className="cls-2" x1="269.341" y1="144.634" x2="269.341" y2="119.153"/>
				</g>
				<g id="LINE-103" data-name="LINE">
					<line className="cls-2" x1="269.34" y1="144.635" x2="269.341" y2="144.634"/>
				</g>
				<g id="LINE-104" data-name="LINE">
					<line className="cls-2" x1="269.34" y1="119.154" x2="269.341" y2="119.153"/>
				</g>
				</g>
			</g>
			<g id="R05" onClick={() => handler('5')} className={classNames(isActive('5') ? 'active' : '', isAvailable('5') ? 'available' : '')}>
				<path className="cls-4" d="m460.104,262.204c-46.954-8.863-85.51-22.729-113.974-39.544-29.432-17.386-48.002-37.955-53.697-59.476-.123-.467-.234-.934-.345-1.401,2.876-11.999,9.87-24.008,21.329-35.66h0v-25.482l-45.556-8.599h0c-18.321,18.63-26.968,38.012-26.97,57.082l-.007.005v25.481c0,5.541.73,11.055,2.167,16.518,6.662,25.344,28.518,49.553,63.209,70.014,33.504,19.762,78.902,36.061,134.218,46.503l19.628-19.959v-25.481h-.002Z"/>
				<g>
				<g id="LINE-105" data-name="LINE">
					<line className="cls-2" x1="306.26" y1="261.141" x2="306.26" y2="235.66"/>
				</g>
				<g id="LINE-106" data-name="LINE">
					<line className="cls-2" x1="346.129" y1="222.66" x2="306.26" y2="235.66"/>
				</g>
				<g id="SPLINE-43" data-name="SPLINE">
					<path className="cls-2" d="m460.104,262.204c-46.954-8.863-85.51-22.729-113.975-39.544"/>
				</g>
				<g id="LINE-107" data-name="LINE">
					<line className="cls-2" x1="460.106" y1="262.204" x2="460.104" y2="262.204"/>
				</g>
				<g id="LINE-108" data-name="LINE">
					<line className="cls-2" x1="460.106" y1="287.685" x2="460.106" y2="262.204"/>
				</g>
				<g id="LINE-109" data-name="LINE">
					<line className="cls-2" x1="440.478" y1="307.644" x2="460.106" y2="287.685"/>
				</g>
				<g id="LINE-110" data-name="LINE">
					<line className="cls-2" x1="440.478" y1="282.163" x2="460.106" y2="262.204"/>
				</g>
				<g id="LINE-111" data-name="LINE">
					<line className="cls-2" x1="440.478" y1="307.644" x2="440.478" y2="282.163"/>
				</g>
				<g id="SPLINE-44" data-name="SPLINE">
					<path className="cls-2" d="m306.26,261.141c33.504,19.762,78.902,36.061,134.218,46.503"/>
				</g>
				<g id="SPLINE-45" data-name="SPLINE">
					<path className="cls-2" d="m306.26,235.66c33.504,19.762,78.902,36.061,134.218,46.503"/>
				</g>
				<g id="SPLINE-46" data-name="SPLINE">
					<path className="cls-2" d="m292.084,161.797c2.874-12.004,9.869-24.017,21.333-35.674"/>
				</g>
				<g id="SPLINE-47" data-name="SPLINE">
					<path className="cls-2" d="m292.433,163.184c-5.46-20.633.912-42.133,20.984-62.543"/>
				</g>
				<g id="LINE-112" data-name="LINE">
					<line className="cls-2" x1="243.051" y1="165.646" x2="292.433" y2="163.184"/>
				</g>
				<g id="LINE-113" data-name="LINE">
					<line className="cls-2" x1="243.051" y1="191.127" x2="243.051" y2="165.646"/>
				</g>
				<g id="SPLINE-48" data-name="SPLINE">
					<path className="cls-2" d="m240.884,174.609c0,5.541.73,11.055,2.166,16.518"/>
				</g>
				<g id="SPLINE-49" data-name="SPLINE">
					<path className="cls-2" d="m267.861,92.041c-23.645,24.045-31.189,49.341-24.81,73.605"/>
				</g>
				<g id="LINE-114" data-name="LINE">
					<line className="cls-2" x1="267.861" y1="92.041" x2="267.861" y2="92.041"/>
				</g>
				<g id="LINE-115" data-name="LINE">
					<line className="cls-2" x1="313.417" y1="100.64" x2="267.861" y2="92.041"/>
				</g>
				<g id="LINE-116" data-name="LINE">
					<line className="cls-2" x1="313.417" y1="126.122" x2="313.417" y2="100.64"/>
				</g>
				<g id="LINE-117" data-name="LINE">
					<line className="cls-2" x1="313.417" y1="126.122" x2="313.417" y2="126.122"/>
				</g>
				<g id="LINE-118" data-name="LINE">
					<line className="cls-2" x1="313.417" y1="100.641" x2="313.417" y2="100.64"/>
				</g>
				<g id="LINE-119" data-name="LINE">
					<line className="cls-2" x1="240.884" y1="174.609" x2="240.884" y2="149.128"/>
				</g>
				<g id="ELLIPSE-17" data-name="ELLIPSE">
					<path className="cls-2" d="m346.129,222.66c-29.432-17.386-48.002-37.955-53.697-59.476"/>
				</g>
				<g id="LINE-120" data-name="LINE">
					<line className="cls-2" x1="306.26" y1="235.66" x2="346.129" y2="222.66"/>
				</g>
				<g id="LINE-121" data-name="LINE">
					<line className="cls-2" x1="306.26" y1="261.141" x2="306.26" y2="235.66"/>
				</g>
				<g id="ELLIPSE-18" data-name="ELLIPSE">
					<path className="cls-2" d="m243.051,191.127c6.662,25.344,28.518,49.553,63.209,70.014"/>
				</g>
				<g id="ELLIPSE-19" data-name="ELLIPSE">
					<path className="cls-2" d="m243.051,165.646c6.662,25.344,28.518,49.553,63.209,70.014"/>
				</g>
				<g id="LINE-122" data-name="LINE">
					<line className="cls-2" x1="243.051" y1="191.127" x2="243.051" y2="165.646"/>
				</g>
				<g id="LINE-123" data-name="LINE">
					<line className="cls-2" x1="292.433" y1="163.184" x2="243.051" y2="165.646"/>
				</g>
				</g>
			</g>
			<g id="R04" onClick={() => handler('4')} className={classNames(isActive('4') ? 'active' : '', isAvailable('4') ? 'available' : '')}>
				<path className="cls-4" d="m479.73,216.763c-29.097-5.492-54.33-13.291-75.123-22.686-21.341-9.642-38.186-21.053-49.54-33.558-7.383-8.131-12.336-16.626-14.801-25.266,2.677-9.309,8.336-18.606,17.224-27.645h0v-25.482l-45.557-8.599h0c-15.208,15.465-22.642,31.536-23.138,47.387-.018.567-.005,1.134-.005,1.701h-.021v25.482c0,15.725,6.826,31.328,20.104,45.953,13.762,15.157,34.203,28.987,60.12,40.676,25.211,11.371,55.81,20.814,91.11,27.477l19.628-19.959v-25.481h-.002Z"/>
				<g>
				<g id="SPLINE-50" data-name="SPLINE">
					<path className="cls-2" d="m479.732,216.763c-29.097-5.492-54.33-13.291-75.123-22.686"/>
				</g>
				<g id="LINE-124" data-name="LINE">
					<line className="cls-2" x1="479.734" y1="216.764" x2="479.732" y2="216.763"/>
				</g>
				<g id="LINE-125" data-name="LINE">
					<line className="cls-2" x1="479.734" y1="242.245" x2="479.734" y2="216.764"/>
				</g>
				<g id="LINE-126" data-name="LINE">
					<line className="cls-2" x1="460.106" y1="262.204" x2="479.734" y2="242.245"/>
				</g>
				<g id="LINE-127" data-name="LINE">
					<line className="cls-2" x1="460.106" y1="236.723" x2="479.734" y2="216.764"/>
				</g>
				<g id="LINE-128" data-name="LINE">
					<line className="cls-2" x1="460.106" y1="262.204" x2="460.106" y2="236.723"/>
				</g>
				<g id="SPLINE-51" data-name="SPLINE">
					<path className="cls-2" d="m368.996,234.727c25.211,11.371,55.81,20.814,91.11,27.477"/>
				</g>
				<g id="SPLINE-52" data-name="SPLINE">
					<path className="cls-2" d="m368.996,209.246c25.211,11.371,55.81,20.814,91.11,27.477"/>
				</g>
				<g id="LINE-129" data-name="LINE">
					<line className="cls-2" x1="368.996" y1="234.727" x2="368.996" y2="209.246"/>
				</g>
				<g id="LINE-130" data-name="LINE">
					<line className="cls-2" x1="404.609" y1="194.077" x2="368.996" y2="209.246"/>
				</g>
				<g id="SPLINE-53" data-name="SPLINE">
					<path className="cls-2" d="m340.26,135.283c2.674-9.319,8.335-18.625,17.233-27.673"/>
				</g>
				<g id="SPLINE-54" data-name="SPLINE">
					<path className="cls-2" d="m338.462,121.223c.382-13.085,6.495-26.346,19.031-39.094"/>
				</g>
				<g id="LINE-131" data-name="LINE">
					<line className="cls-2" x1="338.462" y1="123.846" x2="338.462" y2="121.223"/>
				</g>
				<g id="LINE-132" data-name="LINE">
					<line className="cls-2" x1="288.799" y1="120.916" x2="338.462" y2="121.223"/>
				</g>
				<g id="SPLINE-55" data-name="SPLINE">
					<path className="cls-2" d="m311.937,73.528c-15.208,15.465-22.642,31.536-23.138,47.387"/>
				</g>
				<g id="LINE-133" data-name="LINE">
					<line className="cls-2" x1="311.937" y1="73.528" x2="311.937" y2="73.528"/>
				</g>
				<g id="LINE-134" data-name="LINE">
					<line className="cls-2" x1="357.493" y1="82.127" x2="311.937" y2="73.528"/>
				</g>
				<g id="LINE-135" data-name="LINE">
					<line className="cls-2" x1="357.493" y1="107.609" x2="357.493" y2="82.127"/>
				</g>
				<g id="LINE-136" data-name="LINE">
					<line className="cls-2" x1="357.493" y1="107.609" x2="357.493" y2="107.609"/>
				</g>
				<g id="LINE-137" data-name="LINE">
					<line className="cls-2" x1="357.493" y1="82.128" x2="357.493" y2="82.127"/>
				</g>
				<g id="LINE-138" data-name="LINE">
					<line className="cls-2" x1="338.462" y1="123.846" x2="338.462" y2="121.223"/>
				</g>
				<g id="ELLIPSE-20" data-name="ELLIPSE">
					<path className="cls-2" d="m355.069,160.52c-11.353-12.505-17-25.868-16.607-39.297"/>
				</g>
				<g id="LINE-139" data-name="LINE">
					<line className="cls-2" x1="308.875" y1="168.57" x2="355.069" y2="160.52"/>
				</g>
				<g id="LINE-140" data-name="LINE">
					<line className="cls-2" x1="308.875" y1="194.051" x2="308.875" y2="168.57"/>
				</g>
				<g id="ELLIPSE-21" data-name="ELLIPSE">
					<path className="cls-2" d="m288.772,148.098c0,15.725,6.826,31.328,20.104,45.953"/>
				</g>
				<g id="ELLIPSE-22" data-name="ELLIPSE">
					<path className="cls-2" d="m288.799,120.916c-.51,16.295,6.316,32.497,20.077,47.654"/>
				</g>
				<g id="LINE-141" data-name="LINE">
					<line className="cls-2" x1="338.462" y1="121.223" x2="288.799" y2="120.916"/>
				</g>
				<g id="LINE-142" data-name="LINE">
					<line className="cls-2" x1="288.772" y1="148.098" x2="288.772" y2="122.617"/>
				</g>
				<g id="LINE-143" data-name="LINE">
					<line className="cls-2" x1="308.875" y1="194.051" x2="308.875" y2="168.57"/>
				</g>
				<g id="LINE-144" data-name="LINE">
					<line className="cls-2" x1="355.069" y1="160.52" x2="308.875" y2="168.57"/>
				</g>
				<g id="ELLIPSE-23" data-name="ELLIPSE">
					<path className="cls-2" d="m404.609,194.077c-21.341-9.642-38.186-21.053-49.54-33.558"/>
				</g>
				<g id="LINE-145" data-name="LINE">
					<line className="cls-2" x1="368.996" y1="209.246" x2="404.609" y2="194.077"/>
				</g>
				<g id="LINE-146" data-name="LINE">
					<line className="cls-2" x1="368.996" y1="234.727" x2="368.996" y2="209.246"/>
				</g>
				<g id="ELLIPSE-24" data-name="ELLIPSE">
					<path className="cls-2" d="m308.875,194.051c13.762,15.157,34.203,28.987,60.12,40.676"/>
				</g>
				<g id="ELLIPSE-25" data-name="ELLIPSE">
					<path className="cls-2" d="m308.875,168.57c13.762,15.157,34.203,28.987,60.12,40.676"/>
				</g>
				</g>
			</g>
			<g id="R03" onClick={() => handler('3')} className={classNames(isActive('3') ? 'active' : '', isAvailable('3') ? 'available' : '')}>
				<path className="cls-4" d="m499.36,171.322c-31.302-5.909-57.006-15.153-75.982-26.362-18.096-10.69-30.011-23.202-34.725-36.367,2.378-6.569,6.638-13.113,12.916-19.497h0v-25.482l-45.558-8.599h0c-13.12,13.342-19.293,27.22-19.293,40.877h-.005s0,25.48,0,25.48c0,3.984.526,7.95,1.558,11.877,4.781,18.188,20.43,35.538,45.262,50.184,24.014,14.164,56.552,25.847,96.199,33.33l19.628-19.96v-25.481h-.002Z"/>
				<g>
				<g id="LINE-147" data-name="LINE">
					<line className="cls-2" x1="383.535" y1="183.433" x2="383.535" y2="157.952"/>
				</g>
				<g id="LINE-148" data-name="LINE">
					<line className="cls-2" x1="423.378" y1="144.96" x2="383.535" y2="157.952"/>
				</g>
				<g id="SPLINE-56" data-name="SPLINE">
					<path className="cls-2" d="m499.36,171.322c-31.302-5.909-57.006-15.153-75.982-26.362"/>
				</g>
				<g id="LINE-149" data-name="LINE">
					<line className="cls-2" x1="499.362" y1="171.323" x2="499.36" y2="171.322"/>
				</g>
				<g id="LINE-150" data-name="LINE">
					<line className="cls-2" x1="499.362" y1="196.804" x2="499.362" y2="171.323"/>
				</g>
				<g id="LINE-151" data-name="LINE">
					<line className="cls-2" x1="479.734" y1="216.764" x2="499.362" y2="196.804"/>
				</g>
				<g id="LINE-152" data-name="LINE">
					<line className="cls-2" x1="479.734" y1="191.282" x2="499.362" y2="171.323"/>
				</g>
				<g id="LINE-153" data-name="LINE">
					<line className="cls-2" x1="479.734" y1="216.764" x2="479.734" y2="191.282"/>
				</g>
				<g id="SPLINE-57" data-name="SPLINE">
					<path className="cls-2" d="m383.535,183.433c24.014,14.164,56.552,25.847,96.199,33.33"/>
				</g>
				<g id="SPLINE-58" data-name="SPLINE">
					<path className="cls-2" d="m383.535,157.952c24.014,14.164,56.552,25.847,96.199,33.33"/>
				</g>
				<g id="ELLIPSE-26" data-name="ELLIPSE">
					<path className="cls-2" d="m423.378,144.96c-19.596-11.576-31.964-25.287-35.765-39.652"/>
				</g>
				<g id="LINE-154" data-name="LINE">
					<line className="cls-2" x1="383.535" y1="157.952" x2="423.378" y2="144.96"/>
				</g>
				<g id="LINE-155" data-name="LINE">
					<line className="cls-2" x1="383.535" y1="183.433" x2="383.535" y2="157.952"/>
				</g>
				<g id="ELLIPSE-27" data-name="ELLIPSE">
					<path className="cls-2" d="m338.273,133.249c4.781,18.188,20.43,35.538,45.262,50.184"/>
				</g>
				<g id="ELLIPSE-28" data-name="ELLIPSE">
					<path className="cls-2" d="m338.273,107.768c4.781,18.188,20.43,35.538,45.262,50.184"/>
				</g>
				<g id="LINE-156" data-name="LINE">
					<line className="cls-2" x1="338.273" y1="133.249" x2="338.273" y2="107.768"/>
				</g>
				<g id="LINE-157" data-name="LINE">
					<line className="cls-2" x1="387.614" y1="105.308" x2="338.273" y2="107.768"/>
				</g>
				<g id="SPLINE-59" data-name="SPLINE">
					<path className="cls-2" d="m388.644,108.616c2.376-6.577,6.639-13.129,12.925-19.52"/>
				</g>
				<g id="SPLINE-60" data-name="SPLINE">
					<path className="cls-2" d="m387.614,105.308c-3.64-13.755.575-28.087,13.956-41.693"/>
				</g>
				<g id="LINE-158" data-name="LINE">
					<line className="cls-2" x1="338.273" y1="107.768" x2="387.614" y2="105.308"/>
				</g>
				<g id="LINE-159" data-name="LINE">
					<line className="cls-2" x1="338.273" y1="133.249" x2="338.273" y2="107.768"/>
				</g>
				<g id="SPLINE-61" data-name="SPLINE">
					<path className="cls-2" d="m336.715,121.372c0,3.984.526,7.95,1.558,11.877"/>
				</g>
				<g id="SPLINE-62" data-name="SPLINE">
					<path className="cls-2" d="m356.012,55.015c-16.948,17.234-22.312,35.362-17.739,52.753"/>
				</g>
				<g id="LINE-160" data-name="LINE">
					<line className="cls-2" x1="356.013" y1="55.015" x2="356.012" y2="55.015"/>
				</g>
				<g id="LINE-161" data-name="LINE">
					<line className="cls-2" x1="401.57" y1="63.614" x2="356.013" y2="55.015"/>
				</g>
				<g id="LINE-162" data-name="LINE">
					<line className="cls-2" x1="401.57" y1="89.095" x2="401.57" y2="63.614"/>
				</g>
				<g id="LINE-163" data-name="LINE">
					<line className="cls-2" x1="401.57" y1="89.096" x2="401.57" y2="89.095"/>
				</g>
				<g id="LINE-164" data-name="LINE">
					<line className="cls-2" x1="401.57" y1="63.615" x2="401.57" y2="63.614"/>
				</g>
				<g id="LINE-165" data-name="LINE">
					<line className="cls-2" x1="336.715" y1="121.372" x2="336.715" y2="95.89"/>
				</g>
				</g>
			</g>
			<g id="R02" onClick={() => handler('2')} className={classNames(isActive('2') ? 'active' : '', isAvailable('2') ? 'available' : '')}>
				<path className="cls-4" d="m518.987,125.883c-35.528-6.706-61.516-19.119-74.796-33.747-2.951-3.25-5.27-6.61-6.931-10.04,1.89-3.88,4.67-7.734,8.386-11.513h0v-25.482l-45.558-8.599h0c-10.551,10.729-15.536,21.894-15.537,32.879l-.011.004v25.481c0,10.619,4.668,21.07,13.497,30.795,17.965,19.787,53.159,36.571,101.324,45.663l19.627-19.958v-25.481h-.002Z"/>
				<g>
				<g id="LINE-166" data-name="LINE">
					<line className="cls-2" x1="398.038" y1="125.66" x2="398.038" y2="100.179"/>
				</g>
				<g id="LINE-167" data-name="LINE">
					<line className="cls-2" x1="444.191" y1="92.136" x2="398.038" y2="100.179"/>
				</g>
				<g id="SPLINE-63" data-name="SPLINE">
					<path className="cls-2" d="m518.987,125.883c-35.528-6.706-61.516-19.119-74.796-33.747"/>
				</g>
				<g id="LINE-168" data-name="LINE">
					<line className="cls-2" x1="518.989" y1="125.883" x2="518.987" y2="125.883"/>
				</g>
				<g id="LINE-169" data-name="LINE">
					<line className="cls-2" x1="518.989" y1="151.364" x2="518.989" y2="125.883"/>
				</g>
				<g id="LINE-170" data-name="LINE">
					<line className="cls-2" x1="499.362" y1="171.323" x2="518.989" y2="151.364"/>
				</g>
				<g id="LINE-171" data-name="LINE">
					<line className="cls-2" x1="499.362" y1="145.841" x2="518.989" y2="125.883"/>
				</g>
				<g id="LINE-172" data-name="LINE">
					<line className="cls-2" x1="499.362" y1="171.323" x2="499.362" y2="145.841"/>
				</g>
				<g id="SPLINE-64" data-name="SPLINE">
					<path className="cls-2" d="m398.038,125.66c17.965,19.787,53.159,36.571,101.324,45.663"/>
				</g>
				<g id="SPLINE-65" data-name="SPLINE">
					<path className="cls-2" d="m398.038,100.179c17.965,19.787,53.159,36.571,101.324,45.663"/>
				</g>
				<g id="SPLINE-66" data-name="SPLINE">
					<path className="cls-2" d="m437.258,82.099c1.89-3.881,4.671-7.737,8.388-11.517"/>
				</g>
				<g id="SPLINE-67" data-name="SPLINE">
					<path className="cls-2" d="m444.191,92.136c-13.279-14.628-13.851-31.47,1.455-47.035"/>
				</g>
				<g id="LINE-173" data-name="LINE">
					<line className="cls-2" x1="398.038" y1="100.179" x2="444.191" y2="92.136"/>
				</g>
				<g id="LINE-174" data-name="LINE">
					<line className="cls-2" x1="398.038" y1="125.66" x2="398.038" y2="100.179"/>
				</g>
				<g id="SPLINE-68" data-name="SPLINE">
					<path className="cls-2" d="m384.541,94.865c0,10.619,4.668,21.069,13.497,30.795"/>
				</g>
				<g id="SPLINE-69" data-name="SPLINE">
					<path className="cls-2" d="m400.089,36.501c-20.751,21.102-20.016,43.891-2.051,63.678"/>
				</g>
				<g id="LINE-175" data-name="LINE">
					<line className="cls-2" x1="400.089" y1="36.501" x2="400.089" y2="36.501"/>
				</g>
				<g id="LINE-176" data-name="LINE">
					<line className="cls-2" x1="445.647" y1="45.1" x2="400.089" y2="36.501"/>
				</g>
				<g id="LINE-177" data-name="LINE">
					<line className="cls-2" x1="445.647" y1="70.582" x2="445.647" y2="45.1"/>
				</g>
				<g id="LINE-178" data-name="LINE">
					<line className="cls-2" x1="445.647" y1="70.582" x2="445.647" y2="70.582"/>
				</g>
				<g id="LINE-179" data-name="LINE">
					<line className="cls-2" x1="445.647" y1="45.101" x2="445.647" y2="45.1"/>
				</g>
				<g id="LINE-180" data-name="LINE">
					<line className="cls-2" x1="384.541" y1="94.865" x2="384.541" y2="69.384"/>
				</g>
				</g>
			</g>
			<g id="R0Sb" onClick={() => handler('S')} className={classNames(radiusType == '0' ? 'hidden' : '', isActive('S') ? 'active' : '', isAvailable('S') ? 'available' : '')}>
				<path className="cls-4" d="m489.719,26.589c-5.197,5.285-7.653,10.785-7.653,16.196h-.006v25.481c0,15.872,21.171,30.979,56.554,37.658l37.013-37.638v-25.481l-85.909-16.216Z"/>
				<g>
				<g id="LINE-181" data-name="LINE">
					<line className="cls-2" x1="538.614" y1="105.925" x2="575.627" y2="68.286"/>
				</g>
				<g id="LINE-182" data-name="LINE">
					<line className="cls-2" x1="575.627" y1="68.286" x2="575.627" y2="42.805"/>
				</g>
				<g id="LINE-183" data-name="LINE">
					<line className="cls-2" x1="538.614" y1="80.443" x2="575.627" y2="42.805"/>
				</g>
				<g id="LINE-184" data-name="LINE">
					<line className="cls-2" x1="538.614" y1="105.925" x2="538.614" y2="80.443"/>
				</g>
				<g id="SPLINE-70" data-name="SPLINE">
					<path className="cls-2" d="m482.06,68.267c0,15.872,21.171,30.979,56.554,37.658"/>
				</g>
				<g id="SPLINE-71" data-name="SPLINE">
					<path className="cls-2" d="m489.719,26.589c-20.442,20.787,1.449,44.898,48.896,53.854"/>
				</g>
				<g id="LINE-185" data-name="LINE">
					<line className="cls-2" x1="575.627" y1="42.805" x2="489.719" y2="26.589"/>
				</g>
				<g id="LINE-186" data-name="LINE">
					<line className="cls-2" x1="482.06" y1="68.267" x2="482.06" y2="42.785"/>
				</g>
				</g>
			</g>
			<g id="R01" onClick={() => handler('1')} className={classNames(isActive('1') ? 'active' : '', isAvailable('1') ? 'available' : '')}>
				<path className="cls-4" d="m538.614,80.443c-25.635-4.839-43.815-14.134-51.873-24.935.867-1.155,1.858-2.302,2.976-3.44v-25.481l-45.555-8.599c-7.953,8.087-11.71,16.502-11.711,24.782l-.008.002v25.481c0,24.288,32.398,47.407,86.545,57.628l19.627-19.958-.002-25.481Z"/>
				<g id="LINE-187" data-name="LINE">
				<line className="cls-2" x1="444.164" y1="17.989" x2="444.164" y2="17.989"/>
				</g>
				<g id="LINE-188" data-name="LINE">
				<line className="cls-2" x1="489.718" y1="26.588" x2="444.164" y2="17.989"/>
				</g>
				<g id="LINE-189" data-name="LINE">
				<line className="cls-2" x1="489.718" y1="52.069" x2="489.718" y2="26.588"/>
				</g>
				<g id="LINE-190" data-name="LINE">
				<line className="cls-2" x1="489.718" y1="52.069" x2="489.718" y2="52.069"/>
				</g>
				<g id="LINE-191" data-name="LINE">
				<line className="cls-2" x1="489.718" y1="26.588" x2="489.718" y2="26.588"/>
				</g>
				<g id="SPLINE-72" data-name="SPLINE">
				<path className="cls-2" d="m486.738,55.513c.868-1.156,1.86-2.305,2.98-3.444"/>
				</g>
				<g id="SPLINE-73" data-name="SPLINE">
				<path className="cls-2" d="m538.614,80.443c-47.372-8.942-69.307-33.101-48.897-53.855"/>
				</g>
				<g id="LINE-192" data-name="LINE">
				<line className="cls-2" x1="538.616" y1="80.444" x2="538.614" y2="80.443"/>
				</g>
				<g id="LINE-193" data-name="LINE">
				<line className="cls-2" x1="538.616" y1="105.925" x2="538.616" y2="80.444"/>
				</g>
				<g id="LINE-194" data-name="LINE">
				<line className="cls-2" x1="518.989" y1="125.883" x2="538.616" y2="105.925"/>
				</g>
				<g id="LINE-195" data-name="LINE">
				<line className="cls-2" x1="518.989" y1="100.402" x2="538.616" y2="80.444"/>
				</g>
				<g id="LINE-196" data-name="LINE">
				<line className="cls-2" x1="518.989" y1="125.883" x2="518.989" y2="100.402"/>
				</g>
				<g id="SPLINE-74" data-name="SPLINE">
				<path className="cls-2" d="m432.444,68.255c0,24.288,32.398,47.407,86.545,57.628"/>
				</g>
				<g id="SPLINE-75" data-name="SPLINE">
				<path className="cls-2" d="m444.164,17.989c-31.282,31.81,2.218,68.708,74.825,82.413"/>
				</g>
				<g id="LINE-197" data-name="LINE">
				<line className="cls-2" x1="432.444" y1="68.255" x2="432.444" y2="42.774"/>
				</g>
			</g>
			<g id="R0Sa" onClick={() => handler('S')} className={classNames(radiusType == '0' ? 'hidden' : '', isActive('S') ? 'active' : '', isAvailable('S') ? 'available' : '')}>
				<path className="cls-1" d="m482.795,37.691c.094-.326.198-.652.31-.977-.112.326-.216.651-.31.977Z"/>
				<path className="cls-1" d="m487.097,29.565c.185-.236.38-.472.576-.708-.195.236-.391.471-.576.708Z"/>
				<path className="cls-1" d="m485.655,31.577c.165-.253.341-.506.518-.759-.177.253-.353.505-.518.759Z"/>
				<path className="cls-1" d="m486.345,30.568c.173-.242.357-.482.541-.723-.184.241-.368.482-.541.723Z"/>
				<path className="cls-1" d="m483.505,35.646c.125-.306.259-.613.4-.919-.141.306-.276.612-.4.919Z"/>
				<path className="cls-1" d="m485.025,32.59c.159-.269.328-.537.499-.805-.172.268-.341.537-.499.805Z"/>
				<path className="cls-1" d="m484.458,33.606c.149-.283.309-.565.473-.848-.164.282-.323.565-.473.848Z"/>
				<path className="cls-1" d="m482.531,38.714c.076-.334.163-.667.259-1.001-.096.334-.182.667-.259,1.001Z"/>
				<path className="cls-1" d="m483.12,36.668c.11-.317.23-.634.358-.951-.128.317-.247.634-.358.951Z"/>
				<path className="cls-1" d="m483.951,34.625c.137-.294.285-.588.438-.882-.153.294-.3.588-.438.882Z"/>
				<path className="cls-1" d="m487.909,28.568c.204-.24.419-.48.633-.719-.215.24-.43.479-.633.719Z"/>
				<path className="cls-1" d="m482.53,38.719c-.311,1.358-.464,2.715-.464,4.067h0c0-1.352.153-2.709.464-4.067Z"/>
				<path className="cls-1" d="m488.783,27.575c.301-.329.613-.658.935-.986-.322.328-.634.656-.935.986Z"/>
				<path className="cls-4" d="m483.478,35.717c.01-.024.018-.047.027-.071-.01.024-.018.047-.027.071Z"/>
				<path className="cls-4" d="m482.79,37.713c.002-.007.004-.014.006-.022-.002.007-.004.014-.006.022Z"/>
				<path className="cls-4" d="m483.105,36.714c.005-.015.01-.031.015-.046-.005.015-.01.031-.015.046Z"/>
				<path className="cls-4" d="m482.53,38.719s0-.003,0-.005c0,.002,0,.003,0,.005Z"/>
				<path className="cls-4" d="m488.542,27.848c.081-.091.158-.182.241-.273-.083.091-.159.182-.241.273Z"/>
				<path className="cls-3" d="m538.614,80.443l37.013-37.638-85.909-16.216c-.322.328-.634.656-.935.986-.083.091-.159.182-.241.273-.215.24-.43.479-.633.719-.082.096-.157.193-.237.29-.195.236-.391.471-.576.708-.073.093-.139.186-.21.28-.184.241-.368.482-.541.723-.06.083-.114.167-.172.25-.177.253-.353.505-.518.759-.045.069-.086.139-.13.208-.172.268-.341.537-.499.805-.033.056-.062.112-.095.168-.164.282-.323.565-.473.848-.024.045-.045.091-.069.137-.153.294-.301.588-.438.882-.016.034-.03.069-.046.103-.141.306-.276.612-.4.919-.01.024-.018.047-.027.071-.128.317-.248.634-.358.951-.005.015-.01.031-.015.046-.112.326-.216.651-.31.977-.002.007-.004.014-.006.022-.096.334-.182.667-.259,1.001,0,.002,0,.003,0,.005-.311,1.358-.464,2.715-.464,4.067h0c0,15.872,21.165,30.979,56.548,37.658Z"/>
				<path className="cls-4" d="m487.672,28.858c.08-.097.155-.194.237-.29-.082.096-.157.193-.237.29Z"/>
				<path className="cls-4" d="m484.93,32.758c.032-.056.062-.112.095-.168-.033.056-.062.112-.095.168Z"/>
				<path className="cls-4" d="m483.905,34.727c.016-.034.03-.068.046-.103-.016.034-.03.068-.046.103Z"/>
				<path className="cls-4" d="m484.389,33.742c.024-.045.045-.091.069-.137-.024.045-.045.091-.069.137Z"/>
				<path className="cls-4" d="m486.886,29.845c.071-.093.137-.187.21-.28-.073.093-.139.186-.21.28Z"/>
				<path className="cls-4" d="m486.173,30.818c.058-.083.113-.167.172-.25-.06.083-.114.167-.172.25Z"/>
				<path className="cls-4" d="m485.525,31.785c.044-.069.085-.139.13-.208-.045.069-.086.139-.13.208Z"/>
			</g>
			<g id="R00" onClick={() => handler('0')} className={classNames(radiusType == 'S' ? 'hidden' : '', isActive('0') ? 'active' : '', isAvailable('0') ? 'available' : '')}>
				<path className="cls-4" d="m489.677.709c-5.197,5.285-7.653,10.785-7.653,16.196h-.006v25.481c0,15.872,21.171,30.979,56.554,37.658l37.013-37.638v-25.481L489.677.709Z"/>
				<g>
				<g id="LINE-198" data-name="LINE">
					<line className="cls-2" x1="538.573" y1="80.044" x2="575.586" y2="42.406"/>
				</g>
				<g id="LINE-199" data-name="LINE">
					<line className="cls-2" x1="575.586" y1="42.406" x2="575.586" y2="16.925"/>
				</g>
				<g id="LINE-200" data-name="LINE">
					<line className="cls-2" x1="538.573" y1="54.563" x2="575.586" y2="16.925"/>
				</g>
				<g id="LINE-201" data-name="LINE">
					<line className="cls-2" x1="538.573" y1="80.044" x2="538.573" y2="54.563"/>
				</g>
				<g id="SPLINE-76" data-name="SPLINE">
					<path className="cls-2" d="m482.019,42.386c0,15.872,21.171,30.979,56.554,37.658"/>
				</g>
				<g id="SPLINE-77" data-name="SPLINE">
					<path className="cls-2" d="m489.677.709c-20.442,20.787,1.449,44.898,48.896,53.854"/>
				</g>
				<g id="LINE-202" data-name="LINE">
					<line className="cls-2" x1="575.586" y1="16.925" x2="489.677" y2=".709"/>
				</g>
				<g id="LINE-203" data-name="LINE">
					<line className="cls-2" x1="482.019" y1="42.386" x2="482.019" y2="16.905"/>
				</g>
				</g>
			</g>
			</svg>	
		</>
	}